import { AGENT_REPLYING_END_PURPOSE, AGENT_REPLYING_PURPOSE, AGENT_REPLY_FINAL_PURPOSE, AGENT_REPLY_PREVIEW_END_PURPOSE, AGENT_REPLY_PREVIEW_PURPOSE, CONVERSATION_CONTINUITY_WARNING_PURPOSE, DISPATCH_ACTIVITY_DISPATCHING_PURPOSE, DISPATCH_ACTIVITY_FAILED_PURPOSE, DISPATCH_ACTIVITY_RETRYING_PURPOSE, MESSAGE_MENTION_TYPE_VALUES, REMOTE_AGENT_FAILURE_PURPOSE, collectCanonicalMentionTextFragments, formatCanonicalMentionLiteral, getSpaceMessageAddressingRelationSupportForTargetKind, normalizeMessageEnvelope, normalizeSpacePasswordText, resolveBuiltinAgentControllerBrand, resolveMappedPublicAgentFailureReason, resolveMessageEnvelopeTargetField, resolveSpaceMentionLabels, sanitizeSignalTextPreview, wakeLoopRuntimeProfileIdSchema } from "@wakeloop/schemas";
//#region src/access/space-access-context.ts
function createAuthenticatedSpaceAccessContext(input) {
	return {
		spaceId: input.spaceId,
		actor: {
			kind: "authenticated",
			requestContext: input.requestContext
		},
		proof: createSpaceAccessProof(input.password)
	};
}
function createAnonymousViewerSpaceAccessContext(input) {
	return {
		spaceId: input.spaceId,
		actor: {
			kind: "anonymous_viewer",
			requestId: input.requestId
		},
		proof: createSpaceAccessProof(input.password)
	};
}
function createDispatchContinuationSpaceAccessContext(input) {
	return {
		spaceId: input.spaceId,
		actor: {
			kind: "dispatch_continuation",
			requestContext: input.requestContext,
			dispatchId: input.dispatchId,
			sourceSignalId: input.sourceSignalId,
			targetProfileId: input.targetProfileId,
			...input.attemptId ? { attemptId: input.attemptId } : {}
		},
		proof: createSpaceAccessProof(input.password)
	};
}
function getSpaceAccessRequestContext(spaceAccess) {
	switch (spaceAccess.actor.kind) {
		case "authenticated":
		case "dispatch_continuation": return spaceAccess.actor.requestContext;
		case "anonymous_viewer": return null;
		default: return assertNever$2(spaceAccess.actor);
	}
}
function getSpaceAccessRequestId(spaceAccess) {
	switch (spaceAccess.actor.kind) {
		case "authenticated":
		case "dispatch_continuation": return spaceAccess.actor.requestContext.actorContext.requestId;
		case "anonymous_viewer": return spaceAccess.actor.requestId;
		default: return assertNever$2(spaceAccess.actor);
	}
}
function getSpaceAccessPassword(spaceAccess) {
	switch (spaceAccess.proof.kind) {
		case "none": return;
		case "space_password": return spaceAccess.proof.password;
		default: return assertNever$2(spaceAccess.proof);
	}
}
function toSpaceAccessAuditFields(spaceAccess) {
	return {
		actorKind: spaceAccess.actor.kind,
		hasSpacePasswordProof: spaceAccess.proof.kind === "space_password",
		proofKind: spaceAccess.proof.kind,
		spaceId: spaceAccess.spaceId
	};
}
function createSpaceAccessProof(password) {
	if (password === void 0 || password === null) return { kind: "none" };
	const normalizedPassword = normalizeSpacePasswordText(password);
	if (normalizedPassword.length === 0) return { kind: "none" };
	return {
		kind: "space_password",
		password: normalizedPassword
	};
}
function assertNever$2(value) {
	throw new Error(`Unhandled Space access variant: ${String(value)}`);
}
//#endregion
//#region src/presentation/space-member-agent-views.ts
function resolveSpaceMemberAgentPublicCollaborationView(member) {
	return { agentWakeAvailability: member.agentWakeAvailability ?? null };
}
function resolveSpaceMemberAgentOwnerWakeDiagnosticsView(member) {
	if (member.ownerWakeDiagnostics) return {
		agentExecutionReadiness: member.ownerWakeDiagnostics.agentExecutionReadiness,
		wakeRouteEvidence: member.ownerWakeDiagnostics.wakeRouteEvidence,
		wakeRouteStatus: member.ownerWakeDiagnostics.wakeRouteStatus,
		state: member.ownerWakeDiagnostics.state,
		source: "owner_wake_diagnostics"
	};
	if (member.ownerWakeDiagnostics === null) return {
		agentExecutionReadiness: void 0,
		wakeRouteEvidence: void 0,
		wakeRouteStatus: void 0,
		state: null,
		source: "none"
	};
	return {
		agentExecutionReadiness: void 0,
		wakeRouteEvidence: void 0,
		wakeRouteStatus: void 0,
		state: null,
		source: "none"
	};
}
//#endregion
//#region src/target-identity/controller-display.ts
function normalizeControllerDisplayLabel(agentControllerRef) {
	const normalized = String(agentControllerRef ?? "").trim();
	if (!normalized) return null;
	const withoutBuiltinPrefix = normalized.startsWith("builtin:") ? normalized.slice(8) : normalized;
	return withoutBuiltinPrefix.length > 0 ? withoutBuiltinPrefix : null;
}
//#endregion
//#region src/target-identity/target-catalog.ts
const MENTION_SEARCH_MULTI_SPACE_PATTERN = /\s+/g;
function createSpaceTargetCatalog(snapshot) {
	const activeMembers = buildActiveSpaceMemberInputs(snapshot);
	const ownerProfileNameByOwnerUserId = buildOwnerProfileNameByOwnerUserId(snapshot);
	const labelByProfileId = resolveSpaceMentionLabels(activeMembers.map(({ member }) => ({
		mentionAlias: member.mentionAlias,
		profileId: member.profileId,
		profileName: member.profileName
	})));
	const projectableTargets = activeMembers.map((memberInput) => buildProjectableTarget({
		memberInput,
		ownerProfileNameByOwnerUserId
	}));
	const lookupTokenCounts = buildLookupTokenCounts$1(projectableTargets, { labelByProfileId });
	const entries = projectableTargets.map((target) => buildCatalogEntry({
		labelByProfileId,
		lookupTokenCounts,
		target
	}));
	return {
		byExactMentionLabel: buildExactMentionLabelMap(entries),
		byLookupToken: buildLookupTokenMap(entries),
		byProfileId: new Map(entries.map((entry) => [entry.profileId, entry])),
		entries
	};
}
function listSpaceTargetCatalog(snapshot) {
	return createSpaceTargetCatalog(snapshot).entries;
}
function normalizeReadableMentionLookupToken(value) {
	const normalized = String(value ?? "").trim().replaceAll(/['’]/g, "").replaceAll(/[^A-Za-z0-9_\-\s]+/g, " ").replace(MENTION_SEARCH_MULTI_SPACE_PATTERN, " ").trim().toLowerCase();
	return normalized.length > 0 ? normalized : null;
}
function buildCatalogEntry(input) {
	const mentionLabels = input.labelByProfileId.get(input.target.profileId);
	if (!mentionLabels) throw new Error(`Missing shared mention labels for profile ${input.target.profileId}.`);
	return {
		allowedRelations: input.target.allowedRelations,
		controllerDisplay: input.target.controllerDisplay,
		agentControllerRef: input.target.agentControllerRef,
		exactMentionLabels: mentionLabels.exactMentionLabels,
		labelSource: mentionLabels.labelSource,
		liveNow: input.target.liveNow,
		lookupTokens: buildLookupTokens$1({
			exactMentionLabels: mentionLabels.exactMentionLabels,
			mentionAlias: input.target.mentionAlias,
			profileId: input.target.profileId,
			profileName: input.target.profileName
		}),
		mentionAlias: input.target.mentionAlias,
		ownerDisplay: input.target.ownerDisplay,
		ownerName: input.target.ownerName,
		ownerUserId: input.target.ownerUserId,
		preferredLookupToken: resolvePreferredLookupToken$1({
			exactMentionLabels: mentionLabels.exactMentionLabels,
			lookupTokenCounts: input.lookupTokenCounts,
			profileId: input.target.profileId,
			profileName: input.target.profileName
		}),
		primaryMentionLabel: mentionLabels.primaryMentionLabel,
		profileId: input.target.profileId,
		profileIdShort: input.target.profileIdShort,
		profileKind: input.target.profileKind,
		profileName: input.target.profileName,
		runtimeDeviceDisplayName: input.target.runtimeDeviceDisplayName,
		wakeAvailability: input.target.wakeAvailability
	};
}
function buildProjectableTarget(input) {
	const { member } = input.memberInput;
	const profileKind = resolveCatalogMemberProfileKind(input.memberInput);
	const agentMember = input.memberInput.expectedProfileKind === "agent" ? input.memberInput.member : null;
	const profileId = parseRuntimeProfileId(member.profileId);
	const profileIdShort = buildProfileIdShort$1(profileId);
	const ownerName = normalizeOptionalText$7(member.ownerName);
	const ownerUserId = normalizeOptionalText$7(member.ownerUserId);
	return {
		allowedRelations: buildAllowedRelations(profileKind),
		controllerDisplay: agentMember ? resolveControllerDisplayLabel(agentMember.agentControllerRef) : null,
		agentControllerRef: agentMember ? normalizeOptionalText$7(agentMember.agentControllerRef) : null,
		liveNow: member.liveNow,
		mentionAlias: normalizeOptionalText$7(member.mentionAlias),
		ownerDisplay: resolveOwnerDisplayName({
			member,
			ownerProfileNameByOwnerUserId: input.ownerProfileNameByOwnerUserId
		}),
		ownerName,
		ownerUserId,
		profileId,
		profileIdShort,
		profileKind,
		profileName: member.profileName,
		runtimeDeviceDisplayName: agentMember ? normalizeOptionalText$7(agentMember.runtimeDeviceDisplayName) : null,
		wakeAvailability: resolveWakeAvailability({
			agentMember,
			profileKind
		})
	};
}
function buildAllowedRelations(profileKind) {
	return getSpaceMessageAddressingRelationSupportForTargetKind(profileKind === "agent" ? "agent_profile" : "human_profile");
}
function resolveWakeAvailability(input) {
	if (input.profileKind !== "agent") return {
		status: "unsupported",
		reasonCodes: [],
		reasonText: null
	};
	if (!input.agentMember) throw new Error("Agent Space Message Addressing target is missing.");
	return resolvePublicWakeAvailability(input.agentMember) ?? {
		status: "blocked",
		reasonCodes: [],
		reasonText: "WakeLoop is still checking whether this agent can reply from this computer."
	};
}
function resolvePublicWakeAvailability(agentMember) {
	const { agentWakeAvailability } = resolveSpaceMemberAgentPublicCollaborationView(agentMember);
	if (!agentWakeAvailability) return null;
	return {
		status: agentWakeAvailability.status === "available_for_wake" ? "ready" : "blocked",
		reasonCodes: agentWakeAvailability.reasonCode ? [agentWakeAvailability.reasonCode] : [],
		reasonText: agentWakeAvailability.summary
	};
}
function buildActiveSpaceMemberInputs(snapshot) {
	return [...snapshot.agents.map((member) => ({
		expectedProfileKind: "agent",
		member
	})), ...snapshot.humans.map((member) => ({
		expectedProfileKind: "human",
		member
	}))].filter((input) => input.member.status === "active");
}
function resolveCatalogMemberProfileKind(input) {
	if (input.member.profileKind === input.expectedProfileKind) return input.member.profileKind;
	throw new Error(`Cannot resolve Space Message Addressing target kind for profile ${input.member.profileId}.`);
}
function buildExactMentionLabelMap(entries) {
	const byExactMentionLabel = /* @__PURE__ */ new Map();
	for (const entry of entries) for (const exactMentionLabel of entry.exactMentionLabels) {
		const existingEntry = byExactMentionLabel.get(exactMentionLabel);
		if (existingEntry && existingEntry.profileId !== entry.profileId) throw new Error(`Invariant violation: duplicate exact mention label "${exactMentionLabel}" in target catalog.`);
		byExactMentionLabel.set(exactMentionLabel, entry);
	}
	return byExactMentionLabel;
}
function buildLookupTokenMap(entries) {
	const byLookupToken = /* @__PURE__ */ new Map();
	for (const entry of entries) for (const lookupToken of entry.lookupTokens) {
		const existingEntry = byLookupToken.get(lookupToken);
		if (existingEntry === void 0) {
			byLookupToken.set(lookupToken, entry);
			continue;
		}
		if (existingEntry === null || existingEntry.profileId !== entry.profileId) byLookupToken.set(lookupToken, null);
	}
	return byLookupToken;
}
function buildLookupTokens$1(input) {
	const tokens = /* @__PURE__ */ new Set();
	addLookupToken$1(tokens, input.profileId);
	addLookupToken$1(tokens, input.profileName);
	addLookupToken$1(tokens, input.mentionAlias);
	for (const exactMentionLabel of input.exactMentionLabels) addLookupToken$1(tokens, exactMentionLabel);
	return [...tokens];
}
function buildLookupTokenCounts$1(targets, input) {
	const counts = /* @__PURE__ */ new Map();
	for (const target of targets) {
		const mentionLabels = input.labelByProfileId.get(target.profileId);
		if (!mentionLabels) continue;
		const uniqueLookupTokens = /* @__PURE__ */ new Set();
		addLookupToken$1(uniqueLookupTokens, target.profileId);
		addLookupToken$1(uniqueLookupTokens, target.profileName);
		addLookupToken$1(uniqueLookupTokens, target.mentionAlias);
		addLookupToken$1(uniqueLookupTokens, mentionLabels.primaryMentionLabel);
		for (const exactMentionLabel of mentionLabels.exactMentionLabels) addLookupToken$1(uniqueLookupTokens, exactMentionLabel);
		for (const lookupToken of uniqueLookupTokens) counts.set(lookupToken, (counts.get(lookupToken) ?? 0) + 1);
	}
	return counts;
}
function resolvePreferredLookupToken$1(input) {
	for (const candidate of [input.exactMentionLabels[0], input.profileName]) {
		const normalizedCandidate = normalizeLookupToken$1(candidate);
		if (normalizedCandidate && isUniquelyOwnedLookupToken(input.lookupTokenCounts, normalizedCandidate)) return normalizedCandidate;
	}
	return input.profileId;
}
function isUniquelyOwnedLookupToken(counts, token) {
	return Boolean(token && counts.get(token) === 1);
}
function addLookupToken$1(tokens, value) {
	const normalizedToken = normalizeLookupToken$1(value);
	if (normalizedToken) tokens.add(normalizedToken);
}
function normalizeLookupToken$1(value) {
	return normalizeReadableMentionLookupToken(value);
}
function buildOwnerProfileNameByOwnerUserId(snapshot) {
	const ownerProfileStateByUserId = /* @__PURE__ */ new Map();
	for (const human of snapshot.humans) {
		if (human.status !== "active") continue;
		const ownerUserId = normalizeOptionalText$7(human.ownerUserId);
		const profileName = normalizeOptionalText$7(human.profileName);
		if (!(ownerUserId && profileName)) continue;
		const nextPriority = human.liveNow ? 1 : 0;
		const existing = ownerProfileStateByUserId.get(ownerUserId);
		if (existing && existing.priority > nextPriority) continue;
		ownerProfileStateByUserId.set(ownerUserId, {
			priority: nextPriority,
			profileName
		});
	}
	return new Map([...ownerProfileStateByUserId.entries()].map(([ownerUserId, state]) => [ownerUserId, state.profileName]));
}
function resolveOwnerDisplayName(input) {
	if (input.member.profileKind === "human") return normalizeOptionalText$7(input.member.profileName) ?? normalizeOptionalText$7(input.member.ownerName);
	const ownerName = normalizeOptionalText$7(input.member.ownerName);
	if (ownerName) return ownerName;
	const ownerUserId = normalizeOptionalText$7(input.member.ownerUserId);
	if (!ownerUserId) return null;
	return input.ownerProfileNameByOwnerUserId.get(ownerUserId) ?? null;
}
function buildProfileIdShort$1(profileId) {
	if (profileId.startsWith("agt_")) return `agt_${profileId.slice(4, 9)}`;
	return profileId.slice(0, 5);
}
function parseRuntimeProfileId(profileId) {
	const parsed = wakeLoopRuntimeProfileIdSchema.safeParse(profileId);
	if (!parsed.success) throw new Error(`Invalid WakeLoop runtime profile ID: ${profileId}`);
	return parsed.data;
}
function resolveControllerDisplayLabel(agentControllerRef) {
	return normalizeControllerDisplayLabel(agentControllerRef);
}
function normalizeOptionalText$7(value) {
	const normalized = String(value ?? "").trim();
	return normalized.length > 0 ? normalized : null;
}
//#endregion
//#region src/authoring/bare-mention-diagnostics.ts
const WORD_CHARACTER_PATTERN = /\w/u;
const BARE_MENTION_BOUNDARY_PATTERN = /[\s.,!?;:，。！？；：\]})"'`]/u;
const WHITESPACE_PATTERN = /\s+/g;
const MAX_BARE_MENTION_DIAGNOSTICS = 5;
function listSpaceBareMentionDiagnostics(input) {
	if (!input.text.includes("@")) return [];
	const candidates = buildBareMentionCandidates(createSpaceTargetCatalog(input.snapshot).entries);
	if (candidates.length === 0) return [];
	const maxResults = Math.max(1, Math.min(input.maxResults ?? MAX_BARE_MENTION_DIAGNOSTICS, 20));
	const diagnostics = [];
	const seenStarts = /* @__PURE__ */ new Set();
	const ignoredRanges = normalizeIgnoredRanges(input.ignoredRanges ?? []);
	for (let cursor = 0; cursor < input.text.length; cursor += 1) {
		const atIndex = input.text.indexOf("@", cursor);
		if (atIndex < 0) break;
		cursor = atIndex;
		const ignoredRange = findIgnoredRangeContaining(ignoredRanges, atIndex);
		if (ignoredRange) {
			cursor = Math.max(cursor, ignoredRange.end - 1);
			continue;
		}
		if (!isPotentialBareMentionStart(input.text, atIndex)) continue;
		const match = resolveBareMentionCandidateAt({
			atIndex,
			candidates,
			text: input.text
		});
		if (!match || seenStarts.has(atIndex)) continue;
		seenStarts.add(atIndex);
		diagnostics.push(toBareMentionDiagnostic(match));
		cursor = Math.max(cursor, match.end - 1);
		if (diagnostics.length >= maxResults) break;
	}
	return diagnostics;
}
function normalizeIgnoredRanges(ranges) {
	return ranges.filter((range) => range.start >= 0 && range.end > range.start).map((range) => ({
		end: range.end,
		start: range.start
	})).sort((left, right) => left.start - right.start);
}
function findIgnoredRangeContaining(ranges, index) {
	for (const range of ranges) {
		if (index < range.start) return null;
		if (index >= range.start && index < range.end) return range;
	}
	return null;
}
function buildBareMentionCandidates(entries) {
	const candidatesByLabel = /* @__PURE__ */ new Map();
	for (const entry of entries) {
		const labels = /* @__PURE__ */ new Set([
			entry.profileName,
			entry.primaryMentionLabel,
			...entry.mentionAlias ? [entry.mentionAlias] : [],
			...entry.exactMentionLabels
		]);
		for (const label of labels) {
			const normalized = normalizeLabel(label);
			if (!normalized) continue;
			const key = normalized.toLowerCase();
			const existing = candidatesByLabel.get(key);
			if (existing === void 0) {
				candidatesByLabel.set(key, {
					entry,
					label: normalized
				});
				continue;
			}
			if (existing === null || existing.entry.profileId !== entry.profileId) candidatesByLabel.set(key, null);
		}
	}
	return [...candidatesByLabel.values()].filter((candidate) => candidate !== null).sort((left, right) => right.label.length - left.label.length);
}
function resolveBareMentionCandidateAt(input) {
	const tail = input.text.slice(input.atIndex + 1);
	const lowerTail = tail.toLowerCase();
	for (const candidate of input.candidates) {
		const lowerLabel = candidate.label.toLowerCase();
		if (!lowerTail.startsWith(lowerLabel)) continue;
		const afterLabel = tail.slice(candidate.label.length);
		if (isCanonicalMentionRelationSuffix(afterLabel)) continue;
		if (!isBareMentionBoundary(afterLabel[0] ?? "")) continue;
		const end = input.atIndex + 1 + candidate.label.length;
		return {
			candidate,
			end,
			mentionText: input.text.slice(input.atIndex, end),
			start: input.atIndex
		};
	}
	return null;
}
function toBareMentionDiagnostic(input) {
	const label = input.candidate.entry.primaryMentionLabel;
	const suggestedWakeText = input.candidate.entry.profileKind === "agent" && input.candidate.entry.allowedRelations.wake ? `@${label}(wake)` : null;
	return {
		end: input.end,
		mentionText: input.mentionText,
		profileId: input.candidate.entry.profileId,
		profileKind: input.candidate.entry.profileKind,
		profileName: input.candidate.entry.profileName,
		start: input.start,
		suggestedCcText: `@${label}(cc)`,
		suggestedReferenceText: `@${label}(reference)`,
		suggestedWakeText
	};
}
function isPotentialBareMentionStart(text, atIndex) {
	const previous = atIndex > 0 ? text[atIndex - 1] ?? "" : "";
	return !(previous === "\\" || WORD_CHARACTER_PATTERN.test(previous));
}
function isBareMentionBoundary(value) {
	if (!value) return true;
	return BARE_MENTION_BOUNDARY_PATTERN.test(value);
}
function isCanonicalMentionRelationSuffix(value) {
	return value.startsWith("(wake)") || value.startsWith("(reference)") || value.startsWith("(cc)");
}
function normalizeLabel(value) {
	const normalized = String(value ?? "").replaceAll(WHITESPACE_PATTERN, " ").trim();
	return normalized.length > 0 ? normalized : null;
}
//#endregion
//#region src/authoring/composer-submission.ts
const CANONICAL_MENTION_TYPES = new Set(MESSAGE_MENTION_TYPE_VALUES);
const DUPLICATE_TARGET_ISSUE_PATTERN = /^(activationTargets|referenceTargets|ccTargets) must not contain duplicate profileId$/u;
const DISTINCT_TARGET_ISSUE_PATTERN = /^(activationTargets|referenceTargets|ccTargets) must not reuse profileId from (activationTargets|referenceTargets|ccTargets)$/u;
const INVALID_MENTION_GUIDANCE = "That mention is not valid. Type @ and choose a member from the list.";
const MISSING_MENTION_TARGET_GUIDANCE = "That mention does not match a current member. Type @ and choose a member from the list.";
const INVALID_MENTION_ACTION_GUIDANCE = "Mention action must be Wake, Reference, or CC. Type @ and choose from the list.";
var SpaceComposerSubmissionError = class extends Error {
	targetProfileId;
	code;
	constructor(input) {
		super(input.message);
		this.name = "SpaceComposerSubmissionError";
		this.code = input.code;
		this.targetProfileId = normalizeOptionalText$6(input.targetProfileId);
	}
};
const SPACE_COMPOSER_SUBMISSION_ERROR_CODES = /* @__PURE__ */ new Set([
	"addressing.duplicate_target",
	"addressing.invalid_canonical_mention_syntax",
	"addressing.invalid_message_envelope",
	"addressing.invalid_reply_target",
	"addressing.members_snapshot_missing",
	"addressing.relation_incompatible",
	"addressing.target_kind_unresolved",
	"addressing.target_not_in_current_space",
	"addressing.target_reused_across_relations",
	"addressing.wake_blocked"
]);
function isSpaceComposerSubmissionError(error) {
	return error instanceof SpaceComposerSubmissionError || error !== null && typeof error === "object" && "code" in error && typeof error.code === "string" && SPACE_COMPOSER_SUBMISSION_ERROR_CODES.has(error.code);
}
function prepareSpaceComposerSubmission(input) {
	const canonicalFragments = collectCanonicalMentionTextFragments(input.text);
	const authoredMentionsInTextOrder = normalizeAuthoredMentionsInTextOrder({
		mentions: input.authoredMentionsInTextOrder,
		text: input.text
	});
	const replyToSignalId = normalizeReplyToSignalId(input.replyTo);
	const replyTo = replyToSignalId ? { signalId: replyToSignalId } : null;
	if (canonicalFragments.length === 0 && authoredMentionsInTextOrder.length === 0) {
		const normalizedText = normalizePlainTextOrThrow(input.text);
		return {
			authoredMentionsInTextOrder: [],
			canonicalMentionsInTextOrder: [],
			hasStructuredTargets: false,
			messageEnvelope: createMessageEnvelopeOrNull({
				activationTargets: [],
				ccTargets: [],
				referenceTargets: [],
				...replyToSignalId ? { replyToSignalId } : {},
				text: normalizedText
			}),
			replyTo,
			resolvedStructuredTargets: emptyResolvedStructuredTargets(),
			text: normalizedText
		};
	}
	validateCanonicalMentionSyntaxOrThrow(canonicalFragments);
	if (!input.snapshot) throw createSubmissionError("addressing.members_snapshot_missing", input.missingSnapshotMessage);
	validateSnapshotTargetKindsOrThrow(input.snapshot);
	const catalog = createSpaceTargetCatalog(input.snapshot);
	const envelopeInput = createEnvelopeInput(input.text, replyToSignalId);
	const canonicalMentionsInTextOrder = [];
	const profileIdsInTextOrder = [];
	for (const fragment of canonicalFragments) {
		const parsedLiteral = fragment.parsedLiteral;
		if (!parsedLiteral) throw createSubmissionError("addressing.invalid_canonical_mention_syntax", buildInvalidCanonicalMentionSyntaxMessage(fragment.text));
		const target = catalog.byExactMentionLabel.get(parsedLiteral.name);
		if (!target) throw createSubmissionError("addressing.target_not_in_current_space", MISSING_MENTION_TARGET_GUIDANCE);
		if (!target.allowedRelations[parsedLiteral.type]) throw createSubmissionError("addressing.relation_incompatible", buildInvalidCanonicalMentionRelationMessage({
			mentionText: fragment.text,
			relation: parsedLiteral.type
		}));
		envelopeInput[resolveMessageEnvelopeTargetField(parsedLiteral.type)].push(target.profileId);
		profileIdsInTextOrder.push(target.profileId);
		canonicalMentionsInTextOrder.push({
			profileId: target.profileId,
			text: fragment.text,
			type: parsedLiteral.type
		});
	}
	for (const authoredMention of authoredMentionsInTextOrder) {
		const target = catalog.byProfileId.get(authoredMention.profileId);
		if (!target) throw createSubmissionError("addressing.target_not_in_current_space", MISSING_MENTION_TARGET_GUIDANCE);
		if (!target.allowedRelations[authoredMention.relation]) throw createSubmissionError("addressing.relation_incompatible", buildInvalidCanonicalMentionRelationMessage({
			mentionText: authoredMention.text,
			relation: authoredMention.relation
		}));
		envelopeInput[resolveMessageEnvelopeTargetField(authoredMention.relation)].push(target.profileId);
		profileIdsInTextOrder.push(target.profileId);
	}
	const messageEnvelope = createMessageEnvelopeOrNull(envelopeInput);
	return {
		authoredMentionsInTextOrder,
		canonicalMentionsInTextOrder,
		hasStructuredTargets: profileIdsInTextOrder.length > 0,
		messageEnvelope,
		replyTo,
		resolvedStructuredTargets: {
			canonicalMentionsInTextOrder,
			profileIdsInTextOrder
		},
		text: messageEnvelope?.text ?? normalizePlainTextOrThrow(input.text)
	};
}
function normalizeAuthoredMentionsInTextOrder(input) {
	const normalizedMentions = [];
	for (const mention of input.mentions ?? []) {
		const profileId = normalizeOptionalText$6(mention.profileId);
		const text = normalizeOptionalText$6(mention.text);
		if (!(profileId && text)) throw createSubmissionError("addressing.invalid_message_envelope", "Authored mention target must include a profile and visible mention text.");
		if (!CANONICAL_MENTION_TYPES.has(mention.relation)) throw createSubmissionError("addressing.invalid_message_envelope", INVALID_MENTION_ACTION_GUIDANCE);
		if (!input.text.includes(text)) continue;
		normalizedMentions.push({
			profileId,
			relation: mention.relation,
			text
		});
	}
	return normalizedMentions;
}
function assertValidSpaceCanonicalMentionSyntax$1(text) {
	validateCanonicalMentionSyntaxOrThrow(collectCanonicalMentionTextFragments(text));
}
function spaceTextHasCanonicalMentionAttempts$1(text) {
	return collectCanonicalMentionTextFragments(text).length > 0;
}
function validateCanonicalMentionSyntaxOrThrow(canonicalFragments) {
	for (const fragment of canonicalFragments) {
		if (fragment.parsedLiteral) continue;
		throw createSubmissionError("addressing.invalid_canonical_mention_syntax", buildInvalidCanonicalMentionSyntaxMessage(fragment.text));
	}
}
function emptyResolvedStructuredTargets() {
	return {
		canonicalMentionsInTextOrder: [],
		profileIdsInTextOrder: []
	};
}
function createEnvelopeInput(text, replyToSignalId) {
	return {
		activationTargets: [],
		ccTargets: [],
		referenceTargets: [],
		...replyToSignalId ? { replyToSignalId } : {},
		text
	};
}
function normalizeReplyToSignalId(replyTo) {
	if (!replyTo) return null;
	const signalId = replyTo.signalId.trim();
	if (signalId.length === 0) throw createSubmissionError("addressing.invalid_reply_target", "replyTo.signalId must be a non-empty string.");
	return signalId;
}
function normalizeEnvelopeOrThrow(input) {
	try {
		return normalizeMessageEnvelope(input);
	} catch (error) {
		throw createSubmissionError(resolveCanonicalMentionEnvelopeErrorCode(error), formatCanonicalMentionEnvelopeError(error));
	}
}
function createSubmissionError(code, message, options = {}) {
	return new SpaceComposerSubmissionError({
		code,
		message,
		targetProfileId: options.targetProfileId
	});
}
function validateSnapshotTargetKindsOrThrow(snapshot) {
	for (const member of snapshot.humans) {
		if (member.status !== "active") continue;
		validateSnapshotTargetKindOrThrow({
			expectedKind: "human",
			profileId: member.profileId,
			profileKind: member.profileKind
		});
	}
	for (const member of snapshot.agents) {
		if (member.status !== "active") continue;
		validateSnapshotTargetKindOrThrow({
			expectedKind: "agent",
			profileId: member.profileId,
			profileKind: member.profileKind
		});
	}
}
function validateSnapshotTargetKindOrThrow(input) {
	if (input.profileKind === input.expectedKind) return;
	throw createSubmissionError("addressing.target_kind_unresolved", `Cannot resolve Space Message Addressing target kind for profile ${input.profileId}.`);
}
function normalizePlainTextOrThrow(text) {
	return normalizeEnvelopeOrThrow({
		activationTargets: [],
		ccTargets: [],
		referenceTargets: [],
		text
	}).text;
}
function normalizeOptionalText$6(value) {
	const normalized = String(value ?? "").trim();
	return normalized.length > 0 ? normalized : null;
}
function createMessageEnvelopeOrNull(input) {
	if (!(input.activationTargets.length > 0 || input.referenceTargets.length > 0 || input.ccTargets.length > 0 || input.replyToSignalId)) return null;
	return normalizeEnvelopeOrThrow(input);
}
function buildInvalidCanonicalMentionSyntaxMessage(mentionText) {
	const trimmedMentionText = mentionText.trim();
	const openParenIndex = trimmedMentionText.lastIndexOf("(");
	if (!trimmedMentionText.startsWith("@") || openParenIndex <= 1 || !trimmedMentionText.endsWith(")")) return INVALID_MENTION_GUIDANCE;
	const rawName = trimmedMentionText.slice(1, openParenIndex);
	const rawType = trimmedMentionText.slice(openParenIndex + 1, trimmedMentionText.length - 1);
	if (rawName.trim() !== rawName) return INVALID_MENTION_GUIDANCE;
	const normalizedName = rawName.replaceAll(/\s+/g, " ").trim();
	if (normalizedName.includes("(") || normalizedName.includes(")")) return INVALID_MENTION_GUIDANCE;
	if (!CANONICAL_MENTION_TYPES.has(rawType)) return INVALID_MENTION_ACTION_GUIDANCE;
	return INVALID_MENTION_GUIDANCE;
}
function buildInvalidCanonicalMentionRelationMessage(input) {
	return `Invalid canonical mention "${input.mentionText}": ${input.relation} is only allowed for ${describeRelationSupport(input.relation)} in this space.`;
}
function describeRelationSupport(relation) {
	if (relation === "wake") return "agent targets";
	return "active targets";
}
function formatCanonicalMentionEnvelopeError(error) {
	const issues = extractEnvelopeIssueMessages(error);
	for (const issueMessage of issues) {
		const duplicateMatch = issueMessage.match(DUPLICATE_TARGET_ISSUE_PATTERN);
		if (duplicateMatch?.[1]) return `same target appears more than once in ${toRelationName(duplicateMatch[1])}`;
		const distinctMatch = issueMessage.match(DISTINCT_TARGET_ISSUE_PATTERN);
		if (distinctMatch?.[1] && distinctMatch[2]) return `same target cannot appear in both ${toRelationName(distinctMatch[2])} and ${toRelationName(distinctMatch[1])}`;
	}
	return (issues[0] ?? (error instanceof Error ? error.message.trim() : "Invalid message envelope.")).replaceAll("activationTargets", "wake").replaceAll("referenceTargets", "reference").replaceAll("ccTargets", "cc").replaceAll("profileId", "target");
}
function resolveCanonicalMentionEnvelopeErrorCode(error) {
	const issues = extractEnvelopeIssueMessages(error);
	for (const issueMessage of issues) {
		if (DUPLICATE_TARGET_ISSUE_PATTERN.test(issueMessage)) return "addressing.duplicate_target";
		if (DISTINCT_TARGET_ISSUE_PATTERN.test(issueMessage)) return "addressing.target_reused_across_relations";
	}
	return "addressing.invalid_message_envelope";
}
function extractEnvelopeIssueMessages(error) {
	if (error && typeof error === "object" && "issues" in error && Array.isArray(error.issues)) return error.issues.map((issue) => issue && typeof issue === "object" && "message" in issue && typeof issue.message === "string" ? issue.message.trim() : null).filter((message) => Boolean(message));
	return [];
}
function toRelationName(fieldName) {
	switch (fieldName) {
		case "activationTargets": return "wake";
		case "referenceTargets": return "reference";
		case "ccTargets": return "cc";
		default: return fieldName;
	}
}
//#endregion
//#region src/authoring/member-directory-send-targets.ts
function listSpaceMemberDirectorySendTargets(directory) {
	return [...directory.members];
}
//#endregion
//#region src/authoring/mention-authoring-prefix.ts
const MENTION_START_PATTERN = /[\w\\]/u;
const MENTION_LEADING_WHITESPACE_PATTERN = /^@\s/u;
function extractMentionAuthoringPrefix(textBeforeCursor) {
	if (!textBeforeCursor) return null;
	for (let currentIndex = textBeforeCursor.length - 1; currentIndex >= 0; currentIndex -= 1) {
		if (textBeforeCursor[currentIndex] !== "@") continue;
		const previousCharacter = textBeforeCursor[currentIndex - 1] ?? "";
		if (previousCharacter.length > 0 && MENTION_START_PATTERN.test(previousCharacter)) continue;
		const prefix = textBeforeCursor.slice(currentIndex);
		if (prefix.includes("(") || prefix.includes(")")) return null;
		if (MENTION_LEADING_WHITESPACE_PATTERN.test(prefix)) return null;
		return prefix;
	}
	return null;
}
//#endregion
//#region src/authoring/mention-picker-state.ts
function closeMentionPickerState() {
	return { kind: "closed" };
}
function openTargetPickerState(input) {
	return {
		kind: "target_picker",
		query: input.query,
		selectedProfileId: input.selectedProfileId ?? null
	};
}
function enterRelationPickerState(input) {
	return {
		kind: "relation_picker",
		query: input.query,
		target: input.target
	};
}
function returnToTargetPickerState(state) {
	return openTargetPickerState({
		query: state.query,
		selectedProfileId: state.target.profileId
	});
}
function isRelationPickerState(state) {
	return state.kind === "relation_picker";
}
function isTargetPickerState(state) {
	return state.kind === "target_picker";
}
//#endregion
//#region src/target-identity/mention-text.ts
function collectCanonicalMentionLabelMatches(text) {
	return collectCanonicalMentionTextFragments(text).flatMap((fragment) => {
		if (!fragment.parsedLiteral) return [];
		return [{
			end: fragment.end,
			mentionLabel: fragment.parsedLiteral.name,
			mentionText: fragment.text,
			start: fragment.start,
			type: fragment.parsedLiteral.type
		}];
	});
}
//#endregion
//#region src/target-identity/mention-resolution.ts
function buildSpaceMentionProfileIndex(snapshot) {
	const catalog = createSpaceTargetCatalog(snapshot);
	const profileIdentityByProfileId = /* @__PURE__ */ new Map();
	const byLabelAndRelation = /* @__PURE__ */ new Map();
	for (const [exactMentionLabel, entry] of catalog.byExactMentionLabel) {
		let profileIdentity = profileIdentityByProfileId.get(entry.profileId);
		if (!profileIdentity) {
			profileIdentity = {
				ownerName: entry.ownerName,
				ownerUserId: entry.ownerUserId,
				wakeLoopProfileId: entry.profileId,
				profileName: entry.profileName,
				kind: entry.profileKind
			};
			profileIdentityByProfileId.set(entry.profileId, profileIdentity);
		}
		for (const relation of CANONICAL_MENTION_RELATIONS) {
			if (!entry.allowedRelations[relation]) continue;
			byLabelAndRelation.set(buildMentionRelationKey(exactMentionLabel, relation), profileIdentity);
		}
	}
	return byLabelAndRelation;
}
function collectResolvedSpaceMentions(input) {
	const matches = [];
	for (const match of collectCanonicalMentionLabelMatches(input.text)) {
		const profileIdentity = input.index.get(buildMentionRelationKey(match.mentionLabel, match.type));
		if (!profileIdentity) continue;
		matches.push({
			start: match.start,
			end: match.end,
			mentionText: match.mentionText,
			profileIdentity
		});
	}
	return matches;
}
function listRecentMentionProfileIdsFromText(input) {
	const matches = collectResolvedSpaceMentions(input);
	const recentProfileIds = [];
	const seenProfileIds = /* @__PURE__ */ new Set();
	for (let index = matches.length - 1; index >= 0; index -= 1) {
		const profileId = matches[index]?.profileIdentity.wakeLoopProfileId;
		if (!(profileId && !seenProfileIds.has(profileId))) continue;
		seenProfileIds.add(profileId);
		recentProfileIds.push(profileId);
	}
	return recentProfileIds;
}
const CANONICAL_MENTION_RELATIONS = [
	"wake",
	"reference",
	"cc"
];
function buildMentionRelationKey(mentionLabel, relation) {
	return `${relation}:${mentionLabel}`;
}
//#endregion
//#region src/authoring/send-target-projection.ts
function listSpaceSendTargets(snapshot) {
	return [...listSpaceTargetCatalog(snapshot)];
}
//#endregion
//#region src/authoring/wake-availability-copy.ts
function formatBlockedWakeAvailabilityLabel(wakeAvailability) {
	const reason = wakeAvailability.status === "blocked" ? normalizeReasonText(wakeAvailability.reasonText) : null;
	return reason ? `Unavailable now: ${reason}` : "Unavailable now";
}
function normalizeReasonText(value) {
	const normalized = String(value ?? "").trim();
	if (!normalized) return null;
	return normalized.endsWith(".") ? normalized.slice(0, Math.max(0, normalized.length - 1)) : normalized;
}
//#endregion
//#region src/authoring/mention-suggestions.ts
const MAX_RECENT_MENTION_PROFILE_IDS = 20;
const MENTION_PREFIX_PATTERN = /^@+/;
const MULTI_SPACE_PATTERN = /\s+/g;
const MENTION_RELATION_ORDER = [
	"wake",
	"reference",
	"cc"
];
const RELATION_LABELS = {
	wake: "Wake",
	reference: "Reference",
	cc: "CC"
};
const RELATION_DESCRIPTIONS = {
	wake: "Ask this agent to reply.",
	reference: "Add context without asking for a reply.",
	cc: "Keep them in the loop."
};
function listSpaceMentionSuggestions(input) {
	const snapshot = input.snapshot;
	if (!snapshot) return [];
	const query = normalizeMentionQuery(input.query);
	const recentRankByProfileId = buildRecentMentionRankByProfileId(input.recentProfileIds);
	return listSpaceSendTargets(snapshot).map((target) => buildMentionSuggestionRecord(target)).filter((suggestion) => matchesMentionQuery(suggestion.searchTerms, query)).sort((left, right) => compareMentionSuggestions(left, right, recentRankByProfileId)).map(({ searchTerms: _searchTerms, ...suggestion }) => suggestion);
}
function listSpaceMentionRelationChoices(input) {
	const choices = [];
	for (const relation of MENTION_RELATION_ORDER) {
		if (!input.allowedRelations[relation]) continue;
		choices.push({
			canonicalLiteral: formatCanonicalMentionLiteral({
				name: input.primaryMentionLabel,
				type: relation
			}),
			description: RELATION_DESCRIPTIONS[relation],
			label: RELATION_LABELS[relation],
			relation
		});
	}
	return choices;
}
function normalizeRecentMentionProfileIds(input) {
	const maxRecentProfileIds = resolveMaxRecentProfileIds(input.maxRecentProfileIds);
	const allowedProfileIds = toAllowedProfileIdSet(input.allowedProfileIds);
	const normalizedProfileIds = [];
	const seenProfileIds = /* @__PURE__ */ new Set();
	for (const rawProfileId of input.profileIds ?? []) {
		const profileId = normalizeRecentMentionProfileId(rawProfileId);
		if (!profileId) continue;
		if (allowedProfileIds && !allowedProfileIds.has(profileId)) continue;
		if (seenProfileIds.has(profileId)) continue;
		seenProfileIds.add(profileId);
		normalizedProfileIds.push(profileId);
		if (normalizedProfileIds.length >= maxRecentProfileIds) break;
	}
	return normalizedProfileIds;
}
function mergeRecentMentionProfileIds(input) {
	return normalizeRecentMentionProfileIds({
		profileIds: [...input.usedProfileIds ?? [], ...input.currentRecentProfileIds ?? []],
		maxRecentProfileIds: input.maxRecentProfileIds,
		allowedProfileIds: input.allowedProfileIds
	});
}
function buildRecentMentionRankByProfileId(recentProfileIds) {
	return new Map(normalizeRecentMentionProfileIds({ profileIds: recentProfileIds }).map((profileId, index) => [profileId, index]));
}
function resolveNextRecentMentionProfileIdsForQueuedText(input) {
	const allowedStructuredUsedProfileIds = normalizeRecentMentionProfileIds({
		profileIds: normalizeRecentMentionProfileIds({
			profileIds: [...input.structuredMentionProfileIdsInTextOrder ?? []].toReversed(),
			maxRecentProfileIds: Number.MAX_SAFE_INTEGER
		}),
		maxRecentProfileIds: Number.MAX_SAFE_INTEGER,
		allowedProfileIds: input.allowedProfileIds
	});
	if (allowedStructuredUsedProfileIds.length > 0) return mergeRecentMentionProfileIds({
		currentRecentProfileIds: input.currentRecentProfileIds,
		usedProfileIds: allowedStructuredUsedProfileIds,
		maxRecentProfileIds: input.maxRecentProfileIds,
		allowedProfileIds: input.allowedProfileIds
	});
	const canonicalUsedProfileIds = resolveRecentMentionProfileIdsFromCanonicalMentions(input.canonicalMentionsInTextOrder);
	if (canonicalUsedProfileIds.length > 0) return mergeRecentMentionProfileIds({
		currentRecentProfileIds: input.currentRecentProfileIds,
		usedProfileIds: canonicalUsedProfileIds,
		maxRecentProfileIds: input.maxRecentProfileIds,
		allowedProfileIds: input.allowedProfileIds
	});
	if (!(input.mentionProfileIndex && input.submittedText.length > 0)) return null;
	const usedProfileIds = listRecentMentionProfileIdsFromText({
		index: input.mentionProfileIndex,
		text: input.submittedText
	});
	if (usedProfileIds.length === 0) return null;
	return mergeRecentMentionProfileIds({
		currentRecentProfileIds: input.currentRecentProfileIds,
		usedProfileIds,
		maxRecentProfileIds: input.maxRecentProfileIds,
		allowedProfileIds: input.allowedProfileIds
	});
}
function buildMentionSuggestionRecord(target) {
	return {
		allowedRelations: target.allowedRelations,
		controllerDisplay: target.controllerDisplay,
		agentControllerRef: target.agentControllerRef,
		description: buildMentionSuggestionDescription(target),
		exactMentionLabels: [...target.exactMentionLabels],
		labelSource: target.labelSource,
		liveNow: target.liveNow,
		ownerDisplay: target.ownerDisplay,
		primaryMentionLabel: target.primaryMentionLabel,
		profileId: target.profileId,
		profileKind: target.profileKind,
		profileName: target.profileName,
		wakeAvailability: target.wakeAvailability,
		searchTerms: [
			target.primaryMentionLabel,
			target.profileName,
			target.profileId,
			...target.exactMentionLabels,
			target.preferredLookupToken,
			...target.lookupTokens
		].flatMap((value) => {
			const normalizedValue = normalizeSearchTerm(value);
			return normalizedValue ? [normalizedValue] : [];
		})
	};
}
function buildMentionSuggestionDescription(target) {
	const segments = [];
	if (target.profileKind === "agent" && target.controllerDisplay && !areCaseInsensitiveMatches(target.controllerDisplay, target.primaryMentionLabel)) segments.push(target.controllerDisplay);
	if (target.ownerDisplay) segments.push(target.profileKind === "agent" ? `by ${target.ownerDisplay}` : target.ownerDisplay);
	if (target.profileKind === "agent" && target.wakeAvailability.status === "blocked") segments.push(formatBlockedWakeAvailabilityLabel(target.wakeAvailability));
	return segments.join(" · ");
}
function matchesMentionQuery(searchTerms, query) {
	if (!query) return true;
	return searchTerms.some((value) => value.includes(query));
}
function compareMentionSuggestions(left, right, recentRankByProfileId) {
	if (left.profileKind !== right.profileKind) return left.profileKind === "agent" ? -1 : 1;
	const leftRecentRank = recentRankByProfileId.get(left.profileId) ?? Number.MAX_SAFE_INTEGER;
	const rightRecentRank = recentRankByProfileId.get(right.profileId) ?? Number.MAX_SAFE_INTEGER;
	if (leftRecentRank !== rightRecentRank) return leftRecentRank - rightRecentRank;
	if (left.liveNow !== right.liveNow) return left.liveNow ? -1 : 1;
	return left.primaryMentionLabel.localeCompare(right.primaryMentionLabel, "en", { sensitivity: "base" });
}
function normalizeMentionQuery(value) {
	return value.trim().replace(MENTION_PREFIX_PATTERN, "").replace(MULTI_SPACE_PATTERN, " ").toLowerCase();
}
function normalizeSearchTerm(value) {
	const normalized = String(value ?? "").trim().replace(MENTION_PREFIX_PATTERN, "").replace(MULTI_SPACE_PATTERN, " ").toLowerCase();
	return normalized.length > 0 ? normalized : null;
}
function areCaseInsensitiveMatches(left, right) {
	return left.localeCompare(right, "en", { sensitivity: "accent" }) === 0;
}
function resolveRecentMentionProfileIdsFromCanonicalMentions(canonicalMentionsInTextOrder) {
	if (!(canonicalMentionsInTextOrder && canonicalMentionsInTextOrder.length > 0)) return [];
	return canonicalMentionsInTextOrder.toReversed().map((mention) => mention.profileId);
}
function resolveMaxRecentProfileIds(value) {
	if (typeof value !== "number" || !Number.isInteger(value) || value <= 0) return MAX_RECENT_MENTION_PROFILE_IDS;
	return value;
}
function normalizeRecentMentionProfileId(value) {
	if (typeof value !== "string") return null;
	const normalized = value.trim();
	return normalized.length > 0 ? normalized : null;
}
function toAllowedProfileIdSet(value) {
	if (!value) return null;
	const normalizedProfileIds = normalizeRecentMentionProfileIds({
		profileIds: Array.isArray(value) ? value : [...value],
		maxRecentProfileIds: Number.MAX_SAFE_INTEGER
	});
	return new Set(normalizedProfileIds);
}
//#endregion
//#region src/authoring/typed-submission.ts
const assertValidSpaceCanonicalMentionSyntax = assertValidSpaceCanonicalMentionSyntax$1;
const spaceTextHasCanonicalMentionAttempts = spaceTextHasCanonicalMentionAttempts$1;
function prepareHumanSpaceTypedSubmission(input) {
	const preparedSubmission = prepareSpaceComposerSubmission({
		missingSnapshotMessage: "Cannot validate canonical mentions right now because current space members could not be loaded.",
		snapshot: input.snapshot,
		text: input.text
	});
	return {
		canonicalMentionsInTextOrder: preparedSubmission.resolvedStructuredTargets.canonicalMentionsInTextOrder,
		hasCanonicalMentions: preparedSubmission.resolvedStructuredTargets.canonicalMentionsInTextOrder.length > 0,
		messageEnvelope: preparedSubmission.messageEnvelope,
		resolvedStructuredTargets: preparedSubmission.resolvedStructuredTargets,
		text: preparedSubmission.text
	};
}
function prepareOneShotSpaceTypedSubmission(input) {
	const preparedSubmission = prepareSpaceComposerSubmission({
		missingSnapshotMessage: "Cannot validate mention labels right now because current space members could not be loaded.",
		snapshot: input.snapshot,
		text: input.text
	});
	return {
		canonicalMentionsInTextOrder: preparedSubmission.resolvedStructuredTargets.canonicalMentionsInTextOrder,
		hasStructuredMentions: preparedSubmission.resolvedStructuredTargets.profileIdsInTextOrder.length > 0,
		messageEnvelope: preparedSubmission.messageEnvelope,
		resolvedStructuredTargets: preparedSubmission.resolvedStructuredTargets,
		text: preparedSubmission.text
	};
}
function spaceTextHasOneShotMentionAttempts(text) {
	return spaceTextHasCanonicalMentionAttempts(text);
}
//#endregion
//#region src/membership/profile-add-plan.ts
function buildSpaceProfileAddPlan(input) {
	const actingProfileId = normalizeText$1(input.actingProfileId);
	const currentOwnerUserId = normalizeText$1(input.currentOwnerUserId);
	const activeMemberProfileIds = buildActiveMemberProfileIds(input.currentMembers);
	const candidates = input.profiles.map((profile) => buildSpaceProfileAddCandidate({
		activeMemberProfileIds,
		actingProfileId,
		allowCurrentProfile: input.allowCurrentProfile === true,
		currentOwnerUserId,
		mode: input.mode,
		profile
	})).filter((candidate) => candidate !== null).sort((left, right) => compareSpaceProfileAddCandidates(left, right, input.mode));
	const eligibleCandidateById = new Map(candidates.filter((candidate) => candidate.excludedReason === null).map((candidate) => [candidate.profileId, candidate]));
	const excludedReasonById = new Map(candidates.filter((candidate) => candidate.excludedReason !== null).map((candidate) => [candidate.profileId, candidate.excludedReason]));
	const selectedProfileIds = resolveSelectedProfileIds({
		eligibleCandidateById,
		excludedReasonById,
		selector: input.selector
	});
	const selectedCandidates = selectedProfileIds.map((profileId) => eligibleCandidateById.get(profileId)).filter((candidate) => candidate !== void 0);
	return {
		alreadyJoinedCandidates: candidates.filter((candidate) => candidate.excludedReason === null && candidate.alreadyJoined),
		candidates,
		excludedCandidates: candidates.filter((candidate) => candidate.excludedReason !== null),
		invalidSelections: resolveInvalidSelections({
			eligibleCandidateById,
			excludedReasonById,
			selector: input.selector
		}),
		mode: input.mode,
		selectableCandidates: candidates.filter((candidate) => candidate.selectable),
		selectedCandidates,
		selectedProfileIds
	};
}
function getSpaceProfileAddModeProfileKinds(mode) {
	switch (mode) {
		case "agents": return ["agent"];
		case "human_profiles": return ["human"];
		case "profiles": return ["agent", "human"];
		default: assertNever$1(mode);
	}
}
function buildSpaceProfileAddCandidate(input) {
	const profileId = normalizeText$1(input.profile.profileId);
	const profileName = normalizeText$1(input.profile.profileName);
	const profileKind = normalizeProfileKind$2(input.profile.profileKind);
	if (!(profileId && profileName)) return null;
	const alreadyJoined = input.activeMemberProfileIds.has(profileId);
	const excludedReason = resolveExcludedReason({
		actingProfileId: input.actingProfileId,
		allowCurrentProfile: input.allowCurrentProfile,
		currentOwnerUserId: input.currentOwnerUserId,
		mode: input.mode,
		profile: input.profile,
		profileId,
		profileKind
	});
	return {
		alreadyJoined,
		excludedReason,
		profile: input.profile,
		profileId,
		profileKind,
		profileName,
		selectable: excludedReason === null && !alreadyJoined
	};
}
function resolveExcludedReason(input) {
	if (input.profileKind === null) return "unsupported_profile_kind";
	if (input.profile.status !== "active") return "inactive_profile";
	if (!input.allowCurrentProfile && input.actingProfileId && input.profileId === input.actingProfileId) return "current_acting_profile";
	const ownerUserId = normalizeText$1(input.profile.ownerUserId);
	if (input.currentOwnerUserId && ownerUserId && ownerUserId !== input.currentOwnerUserId) return "different_owner";
	if (!getSpaceProfileAddModeProfileKinds(input.mode).includes(input.profileKind)) return "kind_mismatch";
	return null;
}
function resolveSelectedProfileIds(input) {
	if (input.selector.kind === "none") return [];
	if (input.selector.kind === "all") return [...input.eligibleCandidateById.values()].filter((candidate) => candidate.selectable).map((candidate) => candidate.profileId);
	return normalizeProfileIds(input.selector.profileIds).filter((profileId) => input.eligibleCandidateById.has(profileId));
}
function resolveInvalidSelections(input) {
	if (input.selector.kind !== "profile_ids") return [];
	return normalizeProfileIds(input.selector.profileIds).flatMap((profileId) => {
		if (input.eligibleCandidateById.has(profileId)) return [];
		return [{
			profileId,
			reason: input.excludedReasonById.get(profileId) ?? "not_found"
		}];
	});
}
function buildActiveMemberProfileIds(members) {
	const profileIds = /* @__PURE__ */ new Set();
	for (const member of members) {
		if (member.status === "removed") continue;
		const profileId = normalizeText$1(member.profileId);
		if (profileId) profileIds.add(profileId);
	}
	return profileIds;
}
function compareSpaceProfileAddCandidates(left, right, mode) {
	if (left.alreadyJoined !== right.alreadyJoined) return left.alreadyJoined ? -1 : 1;
	if (mode === "profiles" && left.profileKind !== null && right.profileKind !== null && left.profileKind !== right.profileKind) return left.profileKind === "agent" ? -1 : 1;
	if (left.profile.isDefault !== right.profile.isDefault) return left.profile.isDefault ? -1 : 1;
	const lastUsedDelta = resolveDateMs(right.profile.lastUsedAt) - resolveDateMs(left.profile.lastUsedAt);
	if (lastUsedDelta !== 0) return lastUsedDelta;
	const createdDelta = resolveDateMs(right.profile.createdAt) - resolveDateMs(left.profile.createdAt);
	if (createdDelta !== 0) return createdDelta;
	const nameComparison = left.profileName.localeCompare(right.profileName, "en", { sensitivity: "base" });
	if (nameComparison !== 0) return nameComparison;
	return left.profileId.localeCompare(right.profileId);
}
function normalizeProfileIds(profileIds) {
	const normalized = /* @__PURE__ */ new Set();
	for (const profileId of profileIds) {
		const value = normalizeText$1(profileId);
		if (value) normalized.add(value);
	}
	return [...normalized];
}
function normalizeProfileKind$2(profileKind) {
	return profileKind === "agent" || profileKind === "human" ? profileKind : null;
}
function normalizeText$1(value) {
	const normalized = value?.trim();
	return normalized ? normalized : null;
}
function resolveDateMs(value) {
	if (!value) return 0;
	const parsed = Date.parse(value);
	return Number.isFinite(parsed) ? parsed : 0;
}
function assertNever$1(value) {
	throw new Error(`unexpected space profile add mode: ${String(value)}`);
}
//#endregion
//#region src/presentation/direct-message-space.ts
const WAKELOOP_DIRECT_MESSAGE_SPACE_PURPOSE = "wakeloop_dm_space";
function isDirectMessageSpace(input) {
	return input.purpose === "wakeloop_dm_space" && input.activeMemberCount === 2;
}
function buildDirectMessagePairKey(firstProfileId, secondProfileId) {
	const first = firstProfileId.trim();
	const second = secondProfileId.trim();
	if (!(first && second)) throw new Error("Direct Message pair key requires two profile IDs.");
	return [first, second].sort(compareProfileIds).join(":");
}
function compareProfileIds(left, right) {
	if (left < right) return -1;
	if (left > right) return 1;
	return 0;
}
//#endregion
//#region src/presentation/agent-direct-conversation-space.ts
function isAgentDirectConversationSpace(input) {
	if (input.activeMemberCount !== 2 || input.humanMemberCount !== 1 || input.agentMemberCount !== 1) return false;
	return isDirectMessageSpace({
		activeMemberCount: input.activeMemberCount,
		purpose: input.purpose
	});
}
//#endregion
//#region src/presentation/profile-accent.ts
const BUILTIN_CONTROLLER_DISPLAY_NAME_BY_ID = {
	"claude-code": "Claude Code",
	codex: "Codex",
	opencode: "OpenCode",
	openclaw: "OpenClaw"
};
function resolveSpaceProfileAccent(input) {
	const controllerDisplayName = resolveControllerDisplayName(input.agentControllerRef);
	if (input.profileKind !== "agent") return {
		accentHex: null,
		accentRgb: null,
		controllerDisplayName,
		enabled: false,
		surfaceRgb: null
	};
	const builtinBrand = resolveBuiltinAgentControllerBrand(input.agentControllerRef);
	if (!builtinBrand) return {
		accentHex: null,
		accentRgb: null,
		controllerDisplayName,
		enabled: false,
		surfaceRgb: null
	};
	const [red, green, blue] = builtinBrand.accentRgb;
	return {
		accentHex: builtinBrand.accentHex,
		accentRgb: {
			r: red,
			g: green,
			b: blue
		},
		controllerDisplayName: BUILTIN_CONTROLLER_DISPLAY_NAME_BY_ID[builtinBrand.controllerId] ?? controllerDisplayName,
		enabled: true,
		surfaceRgb: deriveBuiltinControllerBrandSurfaceRgb(builtinBrand)
	};
}
function resolveControllerDisplayName(agentControllerRef) {
	const normalized = String(agentControllerRef ?? "").trim().replaceAll(/\s+/g, " ");
	if (!normalized) return null;
	const builtinBrand = resolveBuiltinAgentControllerBrand(normalized);
	if (builtinBrand) return BUILTIN_CONTROLLER_DISPLAY_NAME_BY_ID[builtinBrand.controllerId];
	if (normalized.startsWith("builtin:")) return normalized.slice(8);
	return normalized;
}
function deriveBuiltinControllerBrandSurfaceRgb(brand) {
	const [red, green, blue] = brand.accentRgb;
	const { hue } = rgbToHsl(red, green, blue);
	return hslToRgb(hue, .24, .18);
}
function hslToRgb(hue, saturation, lightness) {
	const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
	const huePrime = hue / 60;
	const second = chroma * (1 - Math.abs(huePrime % 2 - 1));
	let red = 0;
	let green = 0;
	let blue = 0;
	if (huePrime >= 0 && huePrime < 1) {
		red = chroma;
		green = second;
	} else if (huePrime < 2) {
		red = second;
		green = chroma;
	} else if (huePrime < 3) {
		green = chroma;
		blue = second;
	} else if (huePrime < 4) {
		green = second;
		blue = chroma;
	} else if (huePrime < 5) {
		red = second;
		blue = chroma;
	} else {
		red = chroma;
		blue = second;
	}
	const match = lightness - chroma / 2;
	return {
		r: Math.round((red + match) * 255),
		g: Math.round((green + match) * 255),
		b: Math.round((blue + match) * 255)
	};
}
function rgbToHsl(red, green, blue) {
	const normalizedRed = red / 255;
	const normalizedGreen = green / 255;
	const normalizedBlue = blue / 255;
	const max = Math.max(normalizedRed, normalizedGreen, normalizedBlue);
	const min = Math.min(normalizedRed, normalizedGreen, normalizedBlue);
	const chroma = max - min;
	const lightness = (max + min) / 2;
	if (chroma === 0) return {
		hue: 0,
		lightness,
		saturation: 0
	};
	const saturation = chroma / (1 - Math.abs(2 * lightness - 1));
	let huePrime = 0;
	if (max === normalizedRed) huePrime = (normalizedGreen - normalizedBlue) / chroma;
	else if (max === normalizedGreen) huePrime = (normalizedBlue - normalizedRed) / chroma + 2;
	else huePrime = (normalizedRed - normalizedGreen) / chroma + 4;
	return {
		hue: normalizeHue(huePrime * 60),
		lightness,
		saturation
	};
}
function normalizeHue(value) {
	const normalized = value % 360;
	return normalized >= 0 ? normalized : normalized + 360;
}
//#endregion
//#region src/presentation/space-delete-action.ts
function resolveSpaceDeleteActionVisibility(input) {
	const currentProfileId = normalizeOptionalText$5(input.currentProfileId);
	if (!(currentProfileId && input.membersSnapshot)) return {
		reason: "members_unavailable",
		visible: false
	};
	const members = listActiveMembers(input.membersSnapshot);
	const creatorMember = members.find((member) => member.memberSource === "space_creator");
	if (!creatorMember) return {
		reason: "creator_missing",
		visible: false
	};
	const currentMember = members.find((member) => member.profileId === currentProfileId);
	if (!currentMember) return {
		reason: "current_profile_missing",
		visible: false
	};
	if (creatorMember.profileKind === "human") return creatorMember.profileId === currentProfileId ? {
		reason: "human_creator_profile",
		visible: true
	} : {
		reason: "not_creator_or_owner",
		visible: false
	};
	const creatorOwnerUserId = normalizeOptionalText$5(creatorMember.ownerUserId);
	const currentOwnerUserId = currentMember.profileKind === "human" ? normalizeOptionalText$5(currentMember.ownerUserId) : null;
	if (creatorOwnerUserId && currentOwnerUserId === creatorOwnerUserId) return {
		reason: "agent_creator_owner_human_profile",
		visible: true
	};
	return {
		reason: "not_creator_or_owner",
		visible: false
	};
}
function listActiveMembers(snapshot) {
	return [...snapshot.humans, ...snapshot.agents].filter((member) => member.status === "active");
}
function normalizeOptionalText$5(value) {
	const normalized = String(value ?? "").trim();
	return normalized.length > 0 ? normalized : null;
}
//#endregion
//#region src/presentation/space-member-agent-presence.ts
function buildSpaceMemberAgentPresencePresentation(member) {
	return buildSpaceMemberAgentPublicCollaborationPresencePresentation(member);
}
function buildSpaceMemberAgentOwnerWakeDiagnosticsPresencePresentation(member) {
	const ownerWakeDiagnostics = resolveSpaceMemberAgentOwnerWakeDiagnosticsView(member);
	if (ownerWakeDiagnostics.agentExecutionReadiness) return buildReadinessPresencePresentation({
		liveNow: member.liveNow,
		readiness: ownerWakeDiagnostics.agentExecutionReadiness
	});
	const { agentWakeAvailability } = resolveSpaceMemberAgentPublicCollaborationView(member);
	if (agentWakeAvailability) return buildAgentWakeAvailabilityPresentation({
		liveNow: member.liveNow,
		agentWakeAvailability
	});
	if (member.liveNow) return {
		...fallbackAvailabilityPresentation(),
		detailLabel: "Live agent",
		shortLabel: "live now",
		status: "live"
	};
	if (ownerWakeDiagnostics.wakeRouteEvidence?.status === "unavailable") return {
		...fallbackUnavailableAvailabilityPresentation(),
		detailLabel: "Reply route unavailable",
		shortLabel: "reply route unavailable",
		status: "unavailable"
	};
	return {
		...fallbackCheckingAvailabilityPresentation(),
		detailLabel: "Checking connection",
		shortLabel: "checking connection",
		status: "checking_connection"
	};
}
function buildSpaceMemberAgentPublicCollaborationPresencePresentation(member) {
	const { agentWakeAvailability } = resolveSpaceMemberAgentPublicCollaborationView(member);
	if (agentWakeAvailability) return buildAgentWakeAvailabilityPresentation({
		liveNow: member.liveNow,
		agentWakeAvailability
	});
	if (member.liveNow) return {
		...fallbackAvailabilityPresentation(),
		detailLabel: "Live agent",
		shortLabel: "live now",
		status: "live"
	};
	return {
		...fallbackCheckingAvailabilityPresentation(),
		detailLabel: "Checking connection",
		shortLabel: "checking connection",
		status: "checking_connection"
	};
}
function buildReadinessPresencePresentation(input) {
	if (input.readiness.displayState === "ready") return input.liveNow ? {
		...fallbackAvailabilityPresentation(),
		detailLabel: "Live agent",
		shortLabel: "live now",
		status: "live"
	} : {
		...fallbackAvailabilityPresentation(),
		detailLabel: "Wakeable agent",
		shortLabel: "wakeable",
		status: "wakeable"
	};
	if (hasUnavailableRoute(input.readiness.facts.route.reasonCodes)) return {
		...fallbackUnavailableAvailabilityPresentation(),
		detailLabel: "Reply route unavailable",
		shortLabel: "reply route unavailable",
		status: "unavailable"
	};
	if (input.readiness.displayState === "blocked") return {
		...fallbackUnavailableAvailabilityPresentation(),
		detailLabel: "Reply blocked",
		shortLabel: "reply blocked",
		status: "unavailable"
	};
	return {
		...fallbackCheckingAvailabilityPresentation(),
		detailLabel: "Checking connection",
		shortLabel: "checking connection",
		status: "checking_connection"
	};
}
function buildAgentWakeAvailabilityPresentation(input) {
	if (input.agentWakeAvailability.status === "available_for_wake") return input.liveNow ? {
		...projectAgentWakeAvailability(input.agentWakeAvailability),
		detailLabel: "Live agent",
		shortLabel: "live now",
		status: "live"
	} : {
		...projectAgentWakeAvailability(input.agentWakeAvailability),
		detailLabel: "Wakeable agent",
		shortLabel: "wakeable",
		status: "wakeable"
	};
	if (input.agentWakeAvailability.status === "unavailable_for_wake") return {
		...projectAgentWakeAvailability(input.agentWakeAvailability),
		detailLabel: "Reply route unavailable",
		shortLabel: "reply route unavailable",
		status: "unavailable"
	};
	if (input.agentWakeAvailability.status === "checking_connection") return {
		...projectAgentWakeAvailability(input.agentWakeAvailability),
		detailLabel: "Checking connection",
		shortLabel: "checking connection",
		status: "checking_connection"
	};
	if (input.agentWakeAvailability.status === "offline_for_wake") return {
		...projectAgentWakeAvailability(input.agentWakeAvailability),
		detailLabel: "Offline agent",
		shortLabel: "offline",
		status: "offline"
	};
	return {
		...fallbackCheckingAvailabilityPresentation(),
		detailLabel: "Checking connection",
		shortLabel: "checking connection",
		status: "checking_connection"
	};
}
function projectAgentWakeAvailability(agentWakeAvailability) {
	return {
		availabilityAction: agentWakeAvailability.action,
		availabilityBadgeLabel: agentWakeAvailability.badgeLabel,
		availabilityCategory: agentWakeAvailability.category,
		availabilityIsRefreshing: agentWakeAvailability.isRefreshing,
		availabilityPhase: agentWakeAvailability.phase,
		availabilityReasonCode: agentWakeAvailability.reasonCode,
		availabilitySummary: agentWakeAvailability.summary,
		availabilityTooltip: agentWakeAvailability.tooltip
	};
}
function fallbackAvailabilityPresentation() {
	return {
		availabilityAction: null,
		availabilityBadgeLabel: null,
		availabilityCategory: null,
		availabilityIsRefreshing: false,
		availabilityPhase: null,
		availabilityReasonCode: null,
		availabilitySummary: null,
		availabilityTooltip: null
	};
}
function fallbackCheckingAvailabilityPresentation() {
	return {
		availabilityAction: null,
		availabilityBadgeLabel: "Checking",
		availabilityCategory: "checking",
		availabilityIsRefreshing: true,
		availabilityPhase: "checking_status",
		availabilityReasonCode: null,
		availabilitySummary: "Checking this agent's Wake availability.",
		availabilityTooltip: "Checking Wake availability"
	};
}
function fallbackUnavailableAvailabilityPresentation() {
	return {
		availabilityAction: null,
		availabilityBadgeLabel: "Unavailable",
		availabilityCategory: "unavailable",
		availabilityIsRefreshing: false,
		availabilityPhase: "unavailable",
		availabilityReasonCode: null,
		availabilitySummary: "This agent cannot reply from this computer right now.",
		availabilityTooltip: "Unavailable for Wake"
	};
}
function hasUnavailableRoute(reasonCodes) {
	return reasonCodes.some((reasonCode) => reasonCode === "route.unavailable" || reasonCode === "agent_execution.route_unavailable");
}
//#endregion
//#region src/presentation/space-member-directory.ts
function buildSpaceMemberDirectory(snapshot) {
	const catalog = createSpaceTargetCatalog(snapshot);
	return { sections: [buildDirectorySection({
		catalog,
		members: snapshot.agents,
		sectionId: "agents",
		title: "Agents"
	}), buildDirectorySection({
		catalog,
		members: snapshot.humans,
		sectionId: "humans",
		title: "Humans"
	})].filter((section) => section.entries.length > 0) };
}
function buildDirectorySection(input) {
	return {
		entries: input.members.filter((member) => member.status === "active").map((member) => buildDirectoryEntry(member, input.catalog)).sort(compareDirectoryEntries),
		id: input.sectionId,
		title: input.title
	};
}
function buildDirectoryEntry(member, catalog) {
	const catalogEntry = catalog.byProfileId.get(member.profileId) ?? null;
	const accent = resolveSpaceProfileAccent({
		agentControllerRef: catalogEntry?.agentControllerRef ?? null,
		ownerName: catalogEntry?.ownerName ?? null,
		ownerUserId: catalogEntry?.ownerUserId ?? null,
		profileId: member.profileId,
		profileKind: member.profileKind,
		profileName: member.profileName
	});
	if (member.profileKind === "agent") {
		const presence = buildSpaceMemberAgentPublicCollaborationPresencePresentation(member);
		return {
			addedByDisplayName: normalizeText(member.addedByDisplayName),
			addedByProfileId: normalizeText(member.addedByProfileId),
			avatarUrl: normalizeText(member.avatarUrl),
			agentControllerConversationId: normalizeText(member.agentControllerConversationId),
			agentControllerPermissionMode: member.agentControllerPermissionMode ?? null,
			controllerDisplayName: accent.controllerDisplayName ?? catalogEntry?.controllerDisplay ?? null,
			agentControllerRef: normalizeText(member.agentControllerRef),
			availabilityAction: presence.availabilityAction,
			availabilityBadgeLabel: presence.availabilityBadgeLabel,
			availabilityCategory: presence.availabilityCategory,
			availabilityIsRefreshing: presence.availabilityIsRefreshing,
			availabilityPhase: presence.availabilityPhase,
			availabilityReasonCode: presence.availabilityReasonCode,
			availabilitySummary: presence.availabilitySummary,
			availabilityTooltip: presence.availabilityTooltip,
			id: member.profileId,
			lastSeenLabel: normalizeText(member.lastSeenLabel),
			liveNow: member.liveNow,
			manualParticipationOnly: isManualParticipationOnlyAgent(member),
			mentionLabel: catalogEntry?.mentionAlias ? `@${catalogEntry.mentionAlias}(wake)` : null,
			modelSelection: member.modelSelection ?? null,
			ownerDisplay: catalogEntry?.ownerDisplay ?? null,
			ownerName: catalogEntry?.ownerName ?? null,
			ownerUserId: catalogEntry?.ownerUserId ?? null,
			presenceLabel: presence.shortLabel,
			presenceStatus: presence.status,
			profileId: member.profileId,
			profileKind: member.profileKind,
			profileName: member.profileName,
			runtimeDeviceDisplayName: normalizeText(member.runtimeDeviceDisplayName) ?? catalogEntry?.runtimeDeviceDisplayName ?? null,
			searchText: buildSearchText(member, catalogEntry),
			workingDirectory: member.workingDirectory ?? null
		};
	}
	return {
		addedByDisplayName: normalizeText(member.addedByDisplayName),
		addedByProfileId: normalizeText(member.addedByProfileId),
		avatarUrl: normalizeText(member.avatarUrl),
		agentControllerConversationId: null,
		agentControllerPermissionMode: null,
		controllerDisplayName: null,
		agentControllerRef: null,
		availabilityAction: null,
		availabilityBadgeLabel: null,
		availabilityCategory: null,
		availabilityIsRefreshing: false,
		availabilityPhase: null,
		availabilityReasonCode: null,
		availabilitySummary: null,
		availabilityTooltip: null,
		id: member.profileId,
		lastSeenLabel: normalizeText(member.lastSeenLabel),
		liveNow: member.liveNow,
		manualParticipationOnly: false,
		mentionLabel: null,
		modelSelection: null,
		ownerDisplay: catalogEntry?.ownerDisplay ?? null,
		ownerName: catalogEntry?.ownerName ?? null,
		ownerUserId: catalogEntry?.ownerUserId ?? null,
		presenceLabel: member.liveNow ? "live now" : normalizeText(member.lastSeenLabel),
		presenceStatus: member.liveNow ? "live" : null,
		profileId: member.profileId,
		profileKind: member.profileKind,
		profileName: member.profileName,
		runtimeDeviceDisplayName: null,
		searchText: buildSearchText(member, catalogEntry),
		workingDirectory: null
	};
}
function compareDirectoryEntries(left, right) {
	if (left.liveNow !== right.liveNow) return left.liveNow ? -1 : 1;
	const nameComparison = left.profileName.localeCompare(right.profileName, "en", { sensitivity: "base" });
	if (nameComparison !== 0) return nameComparison;
	return left.profileId.localeCompare(right.profileId);
}
function buildSearchText(member, catalogEntry) {
	return [
		member.profileName,
		member.profileId,
		catalogEntry?.ownerDisplay ?? null,
		catalogEntry?.ownerUserId ?? null,
		catalogEntry?.runtimeDeviceDisplayName ?? null,
		...catalogEntry?.exactMentionLabels ?? []
	].map((value) => normalizeText(value)).filter((value) => value !== null).join(" ").toLowerCase();
}
function normalizeText(value) {
	const normalized = String(value ?? "").trim();
	return normalized.length > 0 ? normalized : null;
}
function isManualParticipationOnlyAgent(member) {
	const readiness = resolveSpaceMemberAgentOwnerWakeDiagnosticsView(member).agentExecutionReadiness ?? null;
	if (readiness) return readiness.displayState !== "ready" && readiness.reasonCodes.some(isUnboundReasonCode);
	return false;
}
function isUnboundReasonCode(reasonCode) {
	return reasonCode === "profile.unbound" || reasonCode === "agent_profile_binding.missing";
}
//#endregion
//#region src/presentation/welcoming-space.ts
const WAKELOOP_WELCOMING_SPACE_PURPOSE = "wakeloop_welcoming_space";
//#endregion
//#region src/read-projection/member-directory.ts
function buildSpaceMemberDirectoryFromIdentityDirectory(identityDirectory) {
	const labelByProfileId = resolveSpaceMentionLabels(identityDirectory.entries.map((entry) => ({
		mentionAlias: entry.mentionAlias,
		profileId: entry.profileId,
		profileName: entry.profileName
	})));
	const lookupTokenCounts = buildLookupTokenCounts(identityDirectory.entries, { labelByProfileId });
	const members = identityDirectory.entries.map((entry) => {
		const labels = labelByProfileId.get(entry.profileId);
		if (!labels) throw new Error(`Missing shared mention labels for profile ${entry.profileId}.`);
		return buildMemberDirectoryEntry({
			entry,
			labels,
			lookupTokenCounts
		});
	}).sort(compareMemberDirectoryEntries);
	return {
		spaceId: identityDirectory.spaceId,
		generatedAt: identityDirectory.generatedAt,
		members
	};
}
function buildMemberDirectoryEntry(input) {
	return {
		profileId: input.entry.profileId,
		profileKind: input.entry.profileKind,
		displayName: input.entry.profileName,
		primaryMentionLabel: input.labels.primaryMentionLabel,
		mentionLabels: [...input.labels.exactMentionLabels],
		mentionAlias: input.entry.mentionAlias,
		labelSource: input.labels.labelSource,
		lookupTokens: buildLookupTokens({
			exactMentionLabels: input.labels.exactMentionLabels,
			mentionAlias: input.entry.mentionAlias,
			profileId: input.entry.profileId,
			profileName: input.entry.profileName
		}),
		preferredLookupToken: resolvePreferredLookupToken({
			exactMentionLabels: input.labels.exactMentionLabels,
			lookupTokenCounts: input.lookupTokenCounts,
			profileId: input.entry.profileId,
			profileName: input.entry.profileName
		}),
		profileIdShort: buildProfileIdShort(input.entry.profileId),
		allowedRelations: getSpaceMessageAddressingRelationSupportForTargetKind(input.entry.profileKind === "agent" ? "agent_profile" : "human_profile"),
		ownerUserId: input.entry.ownerUserId,
		ownerName: input.entry.ownerName,
		agentCard: null,
		spaceRole: null
	};
}
function compareMemberDirectoryEntries(left, right) {
	const labelComparison = left.primaryMentionLabel.localeCompare(right.primaryMentionLabel, "en", { sensitivity: "base" });
	if (labelComparison !== 0) return labelComparison;
	return left.profileId.localeCompare(right.profileId);
}
function buildLookupTokens(input) {
	const tokens = /* @__PURE__ */ new Set();
	addLookupToken(tokens, input.profileId);
	addLookupToken(tokens, input.profileName);
	addLookupToken(tokens, input.mentionAlias);
	for (const exactMentionLabel of input.exactMentionLabels) addLookupToken(tokens, exactMentionLabel);
	return [...tokens];
}
function buildLookupTokenCounts(entries, input) {
	const counts = /* @__PURE__ */ new Map();
	for (const entry of entries) {
		const mentionLabels = input.labelByProfileId.get(entry.profileId);
		if (!mentionLabels) continue;
		const uniqueLookupTokens = /* @__PURE__ */ new Set();
		addLookupToken(uniqueLookupTokens, entry.profileId);
		addLookupToken(uniqueLookupTokens, entry.profileName);
		addLookupToken(uniqueLookupTokens, entry.mentionAlias);
		addLookupToken(uniqueLookupTokens, mentionLabels.primaryMentionLabel);
		for (const exactMentionLabel of mentionLabels.exactMentionLabels) addLookupToken(uniqueLookupTokens, exactMentionLabel);
		for (const lookupToken of uniqueLookupTokens) counts.set(lookupToken, (counts.get(lookupToken) ?? 0) + 1);
	}
	return counts;
}
function resolvePreferredLookupToken(input) {
	for (const candidate of [input.exactMentionLabels[0], input.profileName]) {
		const normalizedCandidate = normalizeLookupToken(candidate);
		if (normalizedCandidate && input.lookupTokenCounts.get(normalizedCandidate) === 1) return normalizedCandidate;
	}
	return input.profileId;
}
function addLookupToken(tokens, value) {
	const normalizedToken = normalizeLookupToken(value);
	if (normalizedToken) tokens.add(normalizedToken);
}
function normalizeLookupToken(value) {
	return normalizeReadableMentionLookupToken(value);
}
function buildProfileIdShort(profileId) {
	if (profileId.startsWith("agt_")) return `agt_${profileId.slice(4, 9)}`;
	return profileId.slice(0, 5);
}
//#endregion
//#region src/read-projection/member-identity-index.ts
function createSpaceMemberIdentityIndex(input) {
	const byProfileId = /* @__PURE__ */ new Map();
	for (const signal of input.signals ?? []) registerSignalIdentity(byProfileId, signal);
	for (const entry of input.identityDirectory?.entries ?? []) registerAuthoritativeIdentityEntry(byProfileId, {
		profileId: entry.profileId,
		profileKind: normalizeProfileKind$1(entry.profileKind),
		profileName: entry.profileName,
		source: "identity_directory"
	});
	for (const member of [...input.snapshot?.humans ?? [], ...input.snapshot?.agents ?? []]) registerAuthoritativeIdentityEntry(byProfileId, {
		profileId: member.profileId,
		profileKind: normalizeProfileKind$1(member.profileKind),
		profileName: member.profileName,
		source: "member_snapshot"
	});
	return { byProfileId };
}
function registerAuthoritativeIdentityEntry(byProfileId, input) {
	const existingEntry = byProfileId.get(input.profileId);
	if (existingEntry?.source === "identity_directory" || existingEntry?.source === "member_snapshot") throw new Error(`Invariant violation: duplicate profile id "${input.profileId}" in space member identity index.`);
	byProfileId.set(input.profileId, input);
}
function registerSignalIdentity(byProfileId, signal) {
	registerIdentityEntry(byProfileId, {
		profileId: normalizeOptionalText$4(signal.profile?.profileId),
		profileKind: normalizeProfileKind$1(signal.profile?.kind),
		profileName: normalizeOptionalText$4(signal.profile?.profileName)
	});
	if (signal.type !== "signal.text") return;
	const targetProfileId = normalizeOptionalText$4(signal.data.meta?.targetProfileId);
	const targetProfileName = normalizeOptionalText$4(signal.data.meta?.targetProfileName);
	if (!(targetProfileId && targetProfileName)) return;
	registerIdentityEntry(byProfileId, {
		profileId: targetProfileId,
		profileKind: normalizeProfileKind$1(signal.data.meta?.targetProfileKind),
		profileName: targetProfileName
	});
}
function registerIdentityEntry(byProfileId, input) {
	if (!(input.profileId && input.profileName)) return;
	const existingEntry = byProfileId.get(input.profileId);
	if (existingEntry?.source === "identity_directory" || existingEntry?.source === "member_snapshot") return;
	byProfileId.set(input.profileId, {
		profileId: input.profileId,
		profileKind: input.profileKind,
		profileName: input.profileName,
		source: "signal_history"
	});
}
function normalizeOptionalText$4(value) {
	const normalized = String(value ?? "").trim();
	return normalized.length > 0 ? normalized : null;
}
function normalizeProfileKind$1(value) {
	return value === "human" || value === "agent" || value === "system" ? value : null;
}
//#endregion
//#region src/read-projection/message-read-projection.ts
function buildSignalIndex(events) {
	return new Map(events.map((event) => [event.id, event]));
}
function buildSignalMessageReadProjection(input) {
	const memberIdentityIndex = buildMemberIdentityIndex(input.identityDirectory ?? null, input.membersSnapshot ?? null, input.signalIndex ?? null);
	const envelope = readSignalMessageEnvelope(input.signal);
	const replyTo = readSignalReplyTo(input.signal);
	const replyToSignalId = normalizeOptionalText$3(envelope?.replyToSignalId ?? replyTo?.signalId);
	return {
		text: readSignalText(input.signal),
		threadRef: input.threadRef ?? null,
		wakeTargets: resolveTargetRefs(envelope?.activationTargets, memberIdentityIndex),
		referenceTargets: resolveTargetRefs(envelope?.referenceTargets, memberIdentityIndex),
		ccTargets: resolveTargetRefs(envelope?.ccTargets, memberIdentityIndex),
		replyToSignalId,
		replyToContext: resolveReplyToContext({
			replyToSignalId,
			signalIndex: input.signalIndex ?? null,
			memberIdentityIndex,
			fallbackReplyTo: replyTo
		})
	};
}
function buildDispatchResultMessageReadProjection(input) {
	const memberIdentityIndex = buildMemberIdentityIndex(input.identityDirectory ?? null, input.membersSnapshot ?? null, input.signalIndex ?? null);
	const envelope = input.result.messageEnvelope ?? null;
	const replyToSignalId = normalizeOptionalText$3(envelope?.replyToSignalId ?? input.result.replyToSignalId);
	return {
		text: normalizeOptionalText$3(envelope?.text ?? input.result.finalText) ?? "",
		threadRef: input.result.threadContext ?? null,
		wakeTargets: resolveTargetRefs(envelope?.activationTargets, memberIdentityIndex),
		referenceTargets: resolveTargetRefs(envelope?.referenceTargets, memberIdentityIndex),
		ccTargets: resolveTargetRefs(envelope?.ccTargets, memberIdentityIndex),
		replyToSignalId,
		replyToContext: resolveReplyToContext({
			replyToSignalId,
			signalIndex: input.signalIndex ?? null,
			memberIdentityIndex
		})
	};
}
function resolveReplyToContext(input) {
	const signalId = normalizeOptionalText$3(input.replyToSignalId);
	if (!signalId) return null;
	const referencedSignal = input.signalIndex?.get(signalId) ?? null;
	if (referencedSignal) return {
		signalId,
		actorProfileId: normalizeOptionalText$3(referencedSignal.profile?.profileId),
		actorProfileKind: normalizeProfileKind(referencedSignal.profile?.kind),
		actorDisplayName: resolveReplyActorDisplayName({
			actorProfileId: normalizeOptionalText$3(referencedSignal.profile?.profileId),
			actorProfileName: normalizeOptionalText$3(referencedSignal.profile?.profileName),
			memberIdentityIndex: input.memberIdentityIndex ?? null
		}),
		previewText: sanitizeSignalTextPreview(readSignalText(referencedSignal)),
		relationSummary: buildSignalRelationSummary({
			signal: referencedSignal,
			memberIdentityIndex: input.memberIdentityIndex ?? null
		}),
		resolved: true
	};
	const fallbackReplyTo = input.fallbackReplyTo ?? null;
	if (fallbackReplyTo && normalizeOptionalText$3(fallbackReplyTo.signalId) === signalId) return {
		signalId,
		actorProfileId: normalizeOptionalText$3(fallbackReplyTo.profileId),
		actorProfileKind: normalizeProfileKind(fallbackReplyTo.profileKind),
		actorDisplayName: resolveReplyActorDisplayName({
			actorProfileId: normalizeOptionalText$3(fallbackReplyTo.profileId),
			actorProfileName: normalizeOptionalText$3(fallbackReplyTo.profileName),
			memberIdentityIndex: input.memberIdentityIndex ?? null
		}),
		previewText: normalizeOptionalText$3(fallbackReplyTo.previewText),
		relationSummary: null,
		resolved: false
	};
	return {
		signalId,
		actorProfileId: null,
		actorProfileKind: null,
		actorDisplayName: null,
		previewText: null,
		relationSummary: null,
		resolved: false
	};
}
function buildSpaceMessageReadRelationSummary(input) {
	const summary = [];
	if (input.wakeTargets.length > 0) summary.push({
		relation: "wake",
		targets: input.wakeTargets
	});
	if (input.referenceTargets.length > 0) summary.push({
		relation: "reference",
		targets: input.referenceTargets
	});
	if (input.ccTargets.length > 0) summary.push({
		relation: "cc",
		targets: input.ccTargets
	});
	return summary;
}
function readSignalMessageEnvelope(signal) {
	if (signal.type !== "signal.text") return null;
	const messageEnvelope = signal.data.messageEnvelope;
	return messageEnvelope && typeof messageEnvelope === "object" ? messageEnvelope : null;
}
function readSignalReplyTo(signal) {
	if (signal.type !== "signal.text" && signal.type !== "signal.status" && signal.type !== "signal.system_notice") return null;
	const replyTo = signal.data.replyTo;
	return replyTo && typeof replyTo === "object" ? replyTo : null;
}
function readSignalText(signal) {
	if (signal.type === "signal.text" || signal.type === "signal.status" || signal.type === "signal.system_notice") return normalizeOptionalText$3(signal.data.text) ?? "";
	return "";
}
function buildSignalRelationSummary(input) {
	const envelope = readSignalMessageEnvelope(input.signal);
	if (!envelope) return null;
	const summary = buildSpaceMessageReadRelationSummary({
		wakeTargets: resolveTargetRefs(envelope.activationTargets, input.memberIdentityIndex),
		referenceTargets: resolveTargetRefs(envelope.referenceTargets, input.memberIdentityIndex),
		ccTargets: resolveTargetRefs(envelope.ccTargets, input.memberIdentityIndex)
	});
	return summary.length > 0 ? summary : null;
}
function resolveTargetRefs(targetProfileIds, memberIdentityIndex) {
	if (!(targetProfileIds && targetProfileIds.length > 0)) return [];
	return targetProfileIds.map((profileId) => resolveTargetRef(profileId, memberIdentityIndex ?? null));
}
function resolveTargetRef(profileId, memberIdentityIndex) {
	const target = resolveTargetByProfileId(profileId, memberIdentityIndex);
	if (!target) return {
		profileId,
		profileKind: null,
		displayName: profileId,
		displaySource: "profile_id",
		resolutionReasonCode: "addressing.target_unresolved",
		resolutionStatus: "unresolved"
	};
	return {
		profileId,
		profileKind: target.profileKind,
		displayName: target.profileName,
		displaySource: target.source,
		...resolveTargetResolution(target)
	};
}
function resolveTargetResolution(target) {
	if (target.profileKind === null) return {
		resolutionReasonCode: "addressing.target_kind_unresolved",
		resolutionStatus: "unresolved"
	};
	if (target.source === "signal_history") return {
		resolutionReasonCode: "addressing.target_stale_signal_history",
		resolutionStatus: "stale"
	};
	return {};
}
function resolveReplyActorDisplayName(input) {
	if (input.actorProfileId) {
		const target = resolveTargetByProfileId(input.actorProfileId, input.memberIdentityIndex);
		if (target) return target.profileName;
	}
	return input.actorProfileName ?? input.actorProfileId ?? null;
}
function resolveTargetByProfileId(profileId, memberIdentityIndex) {
	if (!memberIdentityIndex) return null;
	return memberIdentityIndex.byProfileId.get(profileId) ?? null;
}
function buildMemberIdentityIndex(identityDirectory, membersSnapshot, signalIndex) {
	if (!(identityDirectory || membersSnapshot || signalIndex)) return null;
	return createSpaceMemberIdentityIndex({
		identityDirectory,
		snapshot: identityDirectory ? null : membersSnapshot,
		signals: signalIndex ? signalIndex.values() : null
	});
}
function normalizeOptionalText$3(value) {
	const normalized = String(value ?? "").trim();
	return normalized.length > 0 ? normalized : null;
}
function normalizeProfileKind(value) {
	return value === "human" || value === "agent" || value === "system" ? value : null;
}
//#endregion
//#region src/read-projection/round-read-projection.ts
function buildSpaceRoundReadProjection(input) {
	const sourceSignal = input.sourceSignal ?? input.signalIndex?.get(normalizeRequiredText(input.sourceSignalId)) ?? null;
	const sourceMessageReadProjection = sourceSignal ? buildSignalMessageReadProjection({
		signal: sourceSignal,
		identityDirectory: input.identityDirectory ?? null,
		membersSnapshot: input.membersSnapshot ?? null,
		signalIndex: input.signalIndex ?? null
	}) : null;
	const targets = buildInitialRoundTargets(sourceMessageReadProjection);
	applyDispatchExecutionEvidence({
		targets,
		dispatchExecution: input.dispatchExecution ?? null
	});
	applyPublicationFacts({
		targets,
		facts: buildPublicationFacts({
			dispatchResults: input.dispatchResults ?? [],
			signals: input.signals ?? []
		}),
		sourceSignalId: normalizeRequiredText(input.sourceSignalId)
	});
	return {
		sourceSignalId: normalizeRequiredText(input.sourceSignalId),
		sourceMessageReadProjection,
		targets
	};
}
function buildInitialRoundTargets(sourceMessageReadProjection) {
	if (!sourceMessageReadProjection) return [];
	return [
		...buildRelationTargets({
			dispatchState: null,
			relation: "wake",
			targets: sourceMessageReadProjection.wakeTargets
		}),
		...buildRelationTargets({
			dispatchState: "no_dispatch",
			relation: "reference",
			targets: sourceMessageReadProjection.referenceTargets
		}),
		...buildRelationTargets({
			dispatchState: "no_dispatch",
			relation: "cc",
			targets: sourceMessageReadProjection.ccTargets
		})
	];
}
function buildRelationTargets(input) {
	return input.targets.map((target) => ({
		profileId: target.profileId,
		profileKind: target.profileKind,
		displayName: target.displayName,
		relation: input.relation,
		targetResolutionReasonCode: target.resolutionReasonCode ?? null,
		targetResolutionStatus: target.resolutionStatus ?? null,
		dispatchState: input.dispatchState,
		dispatchId: null,
		dispatchStatus: null,
		latestAttemptStatus: null,
		reasonCode: null,
		reasonText: null,
		publicOutcome: null,
		resultRef: null,
		publishedSignalId: null,
		publishedSignalType: null,
		finalPreview: null
	}));
}
function applyDispatchExecutionEvidence(input) {
	if (!input.dispatchExecution) return;
	for (const targetDispatch of input.dispatchExecution.targetDispatches) {
		const target = input.targets.find((candidate) => candidate.relation === "wake" && candidate.profileId === targetDispatch.targetProfileId) ?? addDispatchOnlyWakeTarget({
			targets: input.targets,
			targetDispatch
		});
		target.dispatchState = targetDispatch.status;
		target.dispatchId = targetDispatch.dispatchId;
		target.dispatchStatus = targetDispatch.dispatchStatus;
		target.latestAttemptStatus = targetDispatch.latestAttemptStatus;
		target.reasonCode = targetDispatch.reasonCode;
		target.reasonText = targetDispatch.reasonText;
	}
}
function addDispatchOnlyWakeTarget(input) {
	const target = {
		profileId: input.targetDispatch.targetProfileId,
		profileKind: null,
		displayName: normalizeOptionalText$2(input.targetDispatch.targetDisplayName) ?? input.targetDispatch.targetProfileId,
		relation: "wake",
		dispatchState: null,
		dispatchId: null,
		dispatchStatus: null,
		latestAttemptStatus: null,
		reasonCode: null,
		reasonText: null,
		publicOutcome: null,
		resultRef: null,
		publishedSignalId: null,
		publishedSignalType: null,
		finalPreview: null,
		targetResolutionReasonCode: null,
		targetResolutionStatus: null
	};
	input.targets.push(target);
	return target;
}
function applyPublicationFacts(input) {
	for (const target of input.targets) {
		const bestFact = selectBestPublicationFact({
			target,
			facts: input.facts,
			sourceSignalId: input.sourceSignalId
		});
		if (!bestFact) continue;
		target.publicOutcome = bestFact.publicOutcome;
		target.resultRef = bestFact.resultRef;
		target.publishedSignalId = bestFact.publishedSignalId;
		target.publishedSignalType = bestFact.publishedSignalType;
		target.finalPreview = bestFact.finalPreview;
	}
}
function selectBestPublicationFact(input) {
	let bestFact = null;
	for (const fact of input.facts) {
		if (!publicationFactMatchesTarget({
			...input,
			fact
		})) continue;
		if (!bestFact || fact.rank > bestFact.rank) bestFact = fact;
	}
	return bestFact;
}
function publicationFactMatchesTarget(input) {
	if (input.target.dispatchId && input.fact.dispatchId) return input.target.dispatchId === input.fact.dispatchId;
	return input.fact.replyToSignalId === input.sourceSignalId && input.fact.actorProfileId === input.target.profileId;
}
function buildPublicationFacts(input) {
	return [...input.signals.flatMap((signal) => {
		const fact = buildSignalPublicationFact(signal);
		return fact ? [fact] : [];
	}), ...input.dispatchResults.map(buildDispatchResultPublicationFact)];
}
function buildSignalPublicationFact(signal) {
	const publicOutcome = resolveSignalPublicOutcome$1(signal);
	if (!publicOutcome) return null;
	return {
		actorProfileId: readSignalActorProfileId(signal),
		dispatchId: readSignalDispatchId$1(signal),
		finalPreview: readSignalFinalPreview(signal),
		publicOutcome,
		publishedSignalId: normalizeOptionalText$2(signal.id),
		publishedSignalType: resolveSignalPublishedSignalType$1(signal),
		rank: rankPublicOutcome(publicOutcome),
		replyToSignalId: readSignalReplyToSignalId(signal),
		resultRef: readSignalResultRef(signal)
	};
}
function buildDispatchResultPublicationFact(result) {
	return {
		actorProfileId: normalizeOptionalText$2(result.actorProfileId),
		dispatchId: normalizeOptionalText$2(result.dispatchId),
		finalPreview: sanitizeSignalTextPreview(result.previewText ?? result.finalText ?? result.statusType),
		publicOutcome: result.publicOutcome,
		publishedSignalId: normalizeOptionalText$2(result.publishedSignalId),
		publishedSignalType: resolveDispatchResultPublishedSignalType$1(result),
		rank: rankPublicOutcome(result.publicOutcome) + 10,
		replyToSignalId: normalizeOptionalText$2(result.replyToSignalId),
		resultRef: normalizeOptionalText$2(result.resultRef)
	};
}
function resolveSignalPublicOutcome$1(signal) {
	switch (signal.type) {
		case "signal.text": return "post_message";
		case "signal.status": return "post_status";
		case "signal.system_notice": return signal.data.noticeType === "stay_silent" ? "stay_silent" : "system_notice";
		default: return null;
	}
}
function resolveSignalPublishedSignalType$1(signal) {
	switch (signal.type) {
		case "signal.text":
		case "signal.status":
		case "signal.system_notice": return signal.type;
		default: return null;
	}
}
function resolveDispatchResultPublishedSignalType$1(result) {
	switch (result.publicOutcome) {
		case "post_message":
		case "raw_context_sharing": return "signal.text";
		case "post_status": return "signal.status";
		case "stay_silent":
		case "system_notice": return "signal.system_notice";
		default: return null;
	}
}
function readSignalActorProfileId(signal) {
	switch (signal.type) {
		case "signal.text": return normalizeOptionalText$2(signal.profile?.profileId);
		case "signal.status": return normalizeOptionalText$2(signal.data.actorProfileId);
		case "signal.system_notice": return normalizeOptionalText$2(signal.data.actorProfileId) ?? normalizeOptionalText$2(signal.profile?.profileId);
		default: return null;
	}
}
function readSignalDispatchId$1(signal) {
	switch (signal.type) {
		case "signal.text": return readSignalTextMetaDispatchId(signal);
		case "signal.status":
		case "signal.system_notice": return normalizeOptionalText$2(signal.data.dispatchId);
		default: return null;
	}
}
function readSignalTextMetaDispatchId(signal) {
	const meta = signal.data.meta;
	if (!meta || typeof meta !== "object" || Array.isArray(meta)) return null;
	return normalizeOptionalText$2(meta.dispatchId);
}
function readSignalFinalPreview(signal) {
	switch (signal.type) {
		case "signal.text":
		case "signal.status":
		case "signal.system_notice": return sanitizeSignalTextPreview(signal.data.text);
		default: return null;
	}
}
function readSignalReplyToSignalId(signal) {
	switch (signal.type) {
		case "signal.text": return normalizeOptionalText$2(signal.data.messageEnvelope?.replyToSignalId ?? signal.data.replyTo?.signalId);
		case "signal.status":
		case "signal.system_notice": return normalizeOptionalText$2(signal.data.replyTo?.signalId);
		default: return null;
	}
}
function readSignalResultRef(signal) {
	switch (signal.type) {
		case "signal.status":
		case "signal.system_notice": return normalizeOptionalText$2(signal.data.resultRef);
		default: return null;
	}
}
function rankPublicOutcome(outcome) {
	switch (outcome) {
		case "post_message":
		case "raw_context_sharing": return 4;
		case "post_status": return 3;
		case "stay_silent": return 2;
		case "system_notice": return 1;
		case "internal_only": return 0;
		default: return 0;
	}
}
function normalizeRequiredText(value) {
	const normalized = String(value).trim();
	if (normalized.length === 0) throw new Error("sourceSignalId is required");
	return normalized;
}
function normalizeOptionalText$2(value) {
	const normalized = String(value ?? "").trim();
	return normalized.length > 0 ? normalized : null;
}
//#endregion
//#region src/reply-workflow/reply-workflow-projection.ts
function buildDispatchResultReplyWorkflowProjection(input) {
	const messageReadProjection = buildDispatchResultMessageReadProjection({
		result: input.result,
		membersSnapshot: input.membersSnapshot ?? null,
		signalIndex: input.signalIndex ?? null
	});
	return {
		sourceSignalId: normalizeOptionalText$1(input.dispatchExecution?.sourceSignalId) ?? normalizeOptionalText$1(messageReadProjection.replyToSignalId),
		replyToSignalId: messageReadProjection.replyToSignalId,
		publicOutcome: input.result.publicOutcome,
		publishedSignalId: normalizeOptionalText$1(input.result.publishedSignalId),
		publishedSignalType: resolveDispatchResultPublishedSignalType(input.result),
		hasPublicSignal: normalizeOptionalText$1(input.result.publishedSignalId) !== null,
		hasPublicAgentReplyText: dispatchResultHasPublicAgentReplyText(input.result),
		dispatchExecution: input.dispatchExecution ?? null,
		replyToContext: messageReadProjection.replyToContext
	};
}
function buildUpdatesReplyWorkflowProjection(input) {
	const memberIdentityIndex = createSpaceMemberIdentityIndex({
		snapshot: input.membersSnapshot ?? null,
		signals: input.signalIndex?.values() ?? null
	});
	const publishedCandidate = selectBestPublishedReplyWorkflowCandidate({
		afterSignalId: input.afterSignalId,
		dispatchExecution: input.dispatchExecution ?? null,
		membersSnapshot: input.membersSnapshot ?? null,
		signalIndex: input.signalIndex ?? null,
		signals: input.signals
	});
	if (publishedCandidate) return publishedCandidate;
	return buildPendingReplyWorkflowProjection({
		afterSignalId: input.afterSignalId,
		dispatchExecution: input.dispatchExecution ?? null,
		memberIdentityIndex,
		signalIndex: input.signalIndex ?? null
	});
}
function buildSignalReplyWorkflowProjection(input) {
	const messageReadProjection = buildSignalMessageReadProjection({
		signal: input.signal,
		membersSnapshot: input.membersSnapshot ?? null,
		signalIndex: input.signalIndex ?? null
	});
	const publishedSignalType = resolveSignalPublishedSignalType(input.signal);
	const publicOutcome = resolveSignalPublicOutcome(input.signal);
	const replyToSignalId = messageReadProjection.replyToSignalId;
	if (publishedSignalType === null || publicOutcome === null || replyToSignalId === null) return null;
	return {
		sourceSignalId: replyToSignalId,
		replyToSignalId,
		publicOutcome,
		publishedSignalId: normalizeOptionalText$1(input.signal.id),
		publishedSignalType,
		hasPublicSignal: true,
		hasPublicAgentReplyText: input.signal.type === "signal.text",
		dispatchExecution: input.dispatchExecution ?? null,
		replyToContext: messageReadProjection.replyToContext
	};
}
function selectBestPublishedReplyWorkflowCandidate(input) {
	const candidateSignals = input.signalIndex === null ? input.signals : [...input.signalIndex.values()];
	const dispatchIds = new Set((input.dispatchExecution?.targetDispatches ?? []).map((targetDispatch) => targetDispatch.dispatchId));
	const hasDispatchEvidence = dispatchIds.size > 0;
	let bestCandidate = null;
	let bestRank = 0;
	for (const signal of candidateSignals) {
		const candidate = buildSignalReplyWorkflowProjection({
			dispatchExecution: input.dispatchExecution,
			membersSnapshot: input.membersSnapshot,
			signal,
			signalIndex: input.signalIndex
		});
		if (!candidate) continue;
		const dispatchId = readSignalDispatchId(signal);
		const matchesDispatch = dispatchId !== null && dispatchIds.has(dispatchId);
		const matchesAnchor = !hasDispatchEvidence && candidate.replyToSignalId === normalizeOptionalText$1(input.afterSignalId);
		if (!(matchesDispatch || matchesAnchor)) continue;
		const candidateRank = rankReplyWorkflowCandidate(candidate);
		if (candidateRank < bestRank) continue;
		bestCandidate = {
			...candidate,
			sourceSignalId: normalizeOptionalText$1(input.afterSignalId) ?? candidate.sourceSignalId
		};
		bestRank = candidateRank;
	}
	return bestCandidate;
}
function buildPendingReplyWorkflowProjection(input) {
	const sourceSignalId = normalizeOptionalText$1(input.afterSignalId);
	const replyToContext = sourceSignalId === null ? null : resolveReplyToContext({
		replyToSignalId: sourceSignalId,
		signalIndex: input.signalIndex,
		memberIdentityIndex: input.memberIdentityIndex
	});
	if (!(input.dispatchExecution || replyToContext)) return null;
	return {
		sourceSignalId,
		replyToSignalId: sourceSignalId,
		publicOutcome: null,
		publishedSignalId: null,
		publishedSignalType: null,
		hasPublicSignal: false,
		hasPublicAgentReplyText: false,
		dispatchExecution: input.dispatchExecution,
		replyToContext
	};
}
function dispatchResultHasPublicAgentReplyText(result) {
	return result.publicOutcome === "post_message" || result.publicOutcome === "raw_context_sharing";
}
function resolveDispatchResultPublishedSignalType(result) {
	switch (result.publicOutcome) {
		case "post_message":
		case "raw_context_sharing": return "signal.text";
		case "post_status": return "signal.status";
		case "stay_silent":
		case "system_notice": return "signal.system_notice";
		default: return null;
	}
}
function resolveSignalPublicOutcome(signal) {
	switch (signal.type) {
		case "signal.text": return "post_message";
		case "signal.status": return "post_status";
		case "signal.system_notice": return readSystemNoticeType(signal) === "stay_silent" ? "stay_silent" : "system_notice";
		default: return null;
	}
}
function resolveSignalPublishedSignalType(signal) {
	switch (signal.type) {
		case "signal.text":
		case "signal.status":
		case "signal.system_notice": return signal.type;
		default: return null;
	}
}
function rankReplyWorkflowCandidate(projection) {
	switch (projection.publicOutcome) {
		case "post_message":
		case "raw_context_sharing": return 4;
		case "post_status": return 3;
		case "stay_silent": return 2;
		case "system_notice": return 1;
		default: return 0;
	}
}
function readSignalDispatchId(signal) {
	switch (signal.type) {
		case "signal.text": return normalizeOptionalText$1(readSignalTextMeta(signal)?.dispatchId);
		case "signal.status":
		case "signal.system_notice": return normalizeOptionalText$1(signal.data.dispatchId);
		default: return null;
	}
}
function readSignalTextMeta(signal) {
	if (!signal.data || typeof signal.data !== "object" || Array.isArray(signal.data)) return null;
	const meta = signal.data.meta;
	if (!meta || typeof meta !== "object" || Array.isArray(meta)) return null;
	return meta;
}
function readSystemNoticeType(signal) {
	return normalizeOptionalText$1(signal.data.noticeType);
}
function normalizeOptionalText$1(value) {
	const normalized = String(value ?? "").trim();
	return normalized.length > 0 ? normalized : null;
}
//#endregion
//#region src/wake-progress/wake-progress-projection.ts
const WAKE_PROGRESS_LOCAL_EXPIRY_MS = 18e4;
const WAKE_PROGRESS_TERMINAL_EXPIRY_MS = 18e4;
const WAKE_PROGRESS_WAITING_ACTIVE_EXPIRY_MS = 48e4;
const WAKE_PROGRESS_WORKING_ACTIVE_EXPIRY_MS = 18e5;
const WAKE_PROGRESS_NO_RESPONSE_MESSAGE = "The agent didn't respond in time.";
const WAKE_PROGRESS_PHASE_RANK = {
	sending: 0,
	request_sent: 1,
	queued: 2,
	connecting: 3,
	running: 4,
	writing: 5,
	publishing: 6,
	retrying: 7,
	failed: 8,
	stopped: 9,
	replied: 10
};
const RUNTIME_LOCATION_PHASES = /* @__PURE__ */ new Set(["connecting", "running"]);
const GENERIC_RUNTIME_LOCATION_PATTERN = /\bon this computer(?:\.{3}|\.)?$/iu;
function buildWakeProgressMessage(input) {
	const targetLabel = formatWakeTargetLabel(input.targetLabel);
	const runtimeLocation = formatWakeProgressRuntimeLocation(input.runtimeDeviceDisplayName);
	switch (input.phase) {
		case "sending": return "Sending message...";
		case "request_sent": return `Wake request sent to ${targetLabel}.`;
		case "queued": return `Starting Wake for ${targetLabel}...`;
		case "connecting": return `Connecting to ${targetLabel} ${runtimeLocation}...`;
		case "running": return `${targetLabel} is working ${runtimeLocation}...`;
		case "writing": return `${targetLabel} started writing...`;
		case "publishing": return `Posting final message for ${targetLabel}...`;
		case "retrying": return `Still trying to reach ${targetLabel}...`;
		case "failed": return buildWakeProgressFailureMessage({
			errorCode: input.errorCode,
			runtimeDeviceDisplayName: input.runtimeDeviceDisplayName,
			targetLabel
		});
		case "stopped": return `Wake was stopped for ${targetLabel}.`;
		case "replied": return `${targetLabel} replied.`;
		default: return assertNever(input.phase);
	}
}
function createWakeProgressItem(draft) {
	const { failureReasonCode, message, runtimeDeviceDisplayName, terminal: draftTerminal, ...itemDraft } = draft;
	const terminal = draftTerminal ?? (draft.phase === "failed" || draft.phase === "stopped" || draft.phase === "replied");
	const normalizedFailureReasonCode = draft.phase === "failed" || draft.phase === "publishing" ? normalizeOptionalText(failureReasonCode) : null;
	return {
		...itemDraft,
		failureReasonCode: normalizedFailureReasonCode,
		targetLabel: formatWakeTargetLabel(draft.targetLabel),
		message: message ?? buildWakeProgressMessage({
			phase: draft.phase,
			runtimeDeviceDisplayName,
			targetLabel: draft.targetLabel
		}),
		terminal
	};
}
function buildWakeProgressItemFromTransientSignal(signal) {
	if (signal.type !== "signal.text") return null;
	const meta = signal.data.meta;
	if (meta?.transient !== true) return null;
	const purpose = normalizeOptionalText(meta.purpose);
	const dispatchId = normalizeOptionalText(meta.dispatchId);
	const targetProfileId = normalizeOptionalText(meta.targetProfileId);
	if (!(purpose && dispatchId && targetProfileId)) return null;
	if (isWakeProgressClearPurpose(purpose)) return null;
	const phase = resolveWakeProgressPhaseFromPurpose(purpose);
	if (!phase) return null;
	const targetLabel = resolveWakeProgressTargetLabel({
		signal,
		targetProfileId
	});
	const runtimeDeviceDisplayName = normalizeOptionalText(meta.runtimeDeviceDisplayName);
	const failureReasonCode = normalizeOptionalText(meta.errorCode);
	return createWakeProgressItem({
		clientMessageId: normalizeOptionalText(meta.sourceClientMessageId),
		dispatchId,
		failureReasonCode: phase === "failed" ? failureReasonCode : null,
		phase,
		sourceSignalId: normalizeOptionalText(meta.sourceEventId),
		targetLabel,
		targetProfileId,
		updatedAtMs: signal.ts,
		runtimeDeviceDisplayName,
		...phase === "failed" || phase === "retrying" ? { message: buildWakeProgressMessage({
			phase,
			runtimeDeviceDisplayName,
			targetLabel,
			errorCode: failureReasonCode
		}) } : {}
	});
}
function mergeWakeProgressItems(input) {
	const itemsByKey = /* @__PURE__ */ new Map();
	for (const item of input.currentItems) itemsByKey.set(buildWakeProgressItemKey(item), item);
	for (const incoming of input.incomingItems) {
		const existingEntry = findMatchingWakeProgressItemEntry({
			itemsByKey,
			incoming
		});
		const key = existingEntry?.[0] ?? buildWakeProgressItemKey(incoming);
		const existing = existingEntry?.[1] ?? null;
		if (incoming.phase === "replied") {
			itemsByKey.delete(key);
			continue;
		}
		if (!existing || shouldReplaceWakeProgressItem({
			existing,
			incoming
		})) if (existingEntry && key !== buildWakeProgressItemKey(incoming)) {
			itemsByKey.delete(key);
			itemsByKey.set(buildWakeProgressItemKey(incoming), incoming);
		} else itemsByKey.set(key, incoming);
	}
	return [...itemsByKey.values()].sort((left, right) => left.updatedAtMs - right.updatedAtMs);
}
function applyWakeProgressSignals(input) {
	const nowMs = input.nowMs ?? Date.now();
	let items = filterVisibleWakeProgressItems({
		items: input.currentItems,
		nowMs
	});
	for (const signal of input.signals) {
		items = clearWakeProgressItemsForSignal({
			items,
			signal
		});
		const nextItem = buildWakeProgressItemFromSignal(signal);
		if (!nextItem) continue;
		items = mergeWakeProgressItems({
			currentItems: items,
			incomingItems: [nextItem]
		});
	}
	return filterVisibleWakeProgressItems({
		items,
		nowMs
	});
}
function filterVisibleWakeProgressItems(input) {
	const nowMs = input.nowMs ?? Date.now();
	return input.items.map((item) => projectWakeProgressItemForPresentation({
		item,
		nowMs
	})).filter((item) => shouldKeepWakeProgressItem({
		item,
		nowMs
	}));
}
function isWakeProgressSourceSignalRecoverable(input) {
	return Number.isFinite(input.sourceUpdatedAtMs) && input.sourceUpdatedAtMs >= 0;
}
function buildWakeProgressItemFromSignal(signal) {
	return buildWakeProgressItemFromTransientSignal(signal) ?? buildWakeProgressItemFromFinalSystemNotice(signal);
}
function buildWakeProgressItemFromFinalSystemNotice(signal) {
	if (signal.type !== "signal.system_notice") return null;
	const noticeType = normalizeOptionalText(signal.data.noticeType);
	if (noticeType !== "terminal_failure" && noticeType !== "wake_stopped") return null;
	const dispatchId = normalizeOptionalText(signal.data.dispatchId);
	const targetProfileId = normalizeOptionalText(signal.data.actorProfileId);
	if (!(dispatchId && targetProfileId)) return null;
	const targetLabel = formatWakeTargetLabel(normalizeOptionalText(signal.data.actorName) ?? targetProfileId);
	const errorCode = normalizeOptionalText(signal.data.reason);
	const phase = noticeType === "wake_stopped" ? "stopped" : "failed";
	return createWakeProgressItem({
		clientMessageId: null,
		dispatchId,
		failureReasonCode: phase === "failed" ? errorCode : null,
		message: buildWakeProgressMessage({
			phase,
			targetLabel,
			errorCode: phase === "failed" ? errorCode : null
		}),
		phase,
		sourceSignalId: normalizeOptionalText(signal.data.replyTo?.signalId),
		targetLabel,
		targetProfileId,
		terminal: true,
		updatedAtMs: signal.ts
	});
}
function clearWakeProgressItemsForSignal(input) {
	if (!isWakeProgressTerminalSignal(input.signal)) return [...input.items];
	return input.items.filter((item) => !doesWakeProgressTerminalSignalMatchItem(input.signal, item));
}
function resolveWakeProgressPhaseFromPurpose(purpose) {
	switch (purpose) {
		case DISPATCH_ACTIVITY_DISPATCHING_PURPOSE: return "queued";
		case DISPATCH_ACTIVITY_RETRYING_PURPOSE: return "retrying";
		case DISPATCH_ACTIVITY_FAILED_PURPOSE: return "failed";
		case AGENT_REPLYING_PURPOSE: return "running";
		case AGENT_REPLY_PREVIEW_PURPOSE: return "writing";
		case CONVERSATION_CONTINUITY_WARNING_PURPOSE: return null;
		default: return null;
	}
}
function isWakeProgressClearPurpose(purpose) {
	return purpose === AGENT_REPLYING_END_PURPOSE || purpose === AGENT_REPLY_PREVIEW_END_PURPOSE;
}
function resolveWakeProgressTargetLabel(input) {
	return formatWakeTargetLabel(normalizeOptionalText(input.signal.data.meta?.targetMentionLabel) ?? normalizeOptionalText(input.signal.data.meta?.targetProfileName) ?? input.targetProfileId);
}
function buildWakeProgressFailureMessage(input) {
	const runtimeLocation = formatWakeProgressRuntimeLocation(input.runtimeDeviceDisplayName);
	const normalizedErrorCode = normalizeOptionalText(input.errorCode)?.toLowerCase();
	switch (normalizedErrorCode) {
		case "daemon.profile.offline":
		case "daemon.session.not_found": return `${input.targetLabel} is offline ${runtimeLocation}.`;
		case "auth.required":
		case "permission.denied.runtime":
		case "quota.exceeded":
		case "rate_limited": return `${input.targetLabel} needs access before it can reply.`;
		case "dispatch.storage_not_ready":
		case "dispatch.storage_unavailable": return "WakeLoop needs a storage update before Wake can run.";
		case "final_visible_outcome.publication_unresolved": return `WakeLoop could not post the final message for ${input.targetLabel}.`;
		default: break;
	}
	const publicFailureReason = resolveMappedPublicAgentFailureReason(input.errorCode);
	if (publicFailureReason) return publicFailureReason;
	switch (normalizedErrorCode) {
		case "auth.required":
		case "permission.denied.runtime":
		case "quota.exceeded":
		case "rate_limited": return `${input.targetLabel} needs access before it can reply.`;
		default: return `${input.targetLabel} couldn't reply.`;
	}
}
function shouldReplaceWakeProgressItem(input) {
	if (input.incoming.phase === input.existing.phase && RUNTIME_LOCATION_PHASES.has(input.incoming.phase)) {
		const specificity = compareWakeProgressRuntimeSpecificity({
			existingMessage: input.existing.message,
			incomingMessage: input.incoming.message
		});
		if (specificity === "incoming") return true;
		if (specificity === "existing") return false;
	}
	if (input.incoming.updatedAtMs >= input.existing.updatedAtMs) return true;
	return WAKE_PROGRESS_PHASE_RANK[input.incoming.phase] > WAKE_PROGRESS_PHASE_RANK[input.existing.phase];
}
function compareWakeProgressRuntimeSpecificity(input) {
	const existingUsesGenericLocation = GENERIC_RUNTIME_LOCATION_PATTERN.test(input.existingMessage);
	const incomingUsesGenericLocation = GENERIC_RUNTIME_LOCATION_PATTERN.test(input.incomingMessage);
	if (existingUsesGenericLocation === incomingUsesGenericLocation) return "same";
	return incomingUsesGenericLocation ? "existing" : "incoming";
}
function shouldKeepWakeProgressItem(input) {
	const ageMs = input.nowMs - input.item.updatedAtMs;
	if (input.item.terminal) return (input.item.phase === "failed" || input.item.phase === "stopped") && ageMs <= WAKE_PROGRESS_TERMINAL_EXPIRY_MS;
	if (input.item.phase === "sending") return ageMs <= WAKE_PROGRESS_LOCAL_EXPIRY_MS;
	const activeExpiryMs = resolveWakeProgressActiveExpiryMs(input.item.phase);
	return activeExpiryMs !== null && ageMs <= activeExpiryMs;
}
function projectWakeProgressItemForPresentation(input) {
	if (input.item.terminal) return input.item;
	const activeExpiryMs = resolveWakeProgressActiveExpiryMs(input.item.phase);
	if (activeExpiryMs === null || input.nowMs - input.item.updatedAtMs <= activeExpiryMs) return input.item;
	return {
		...input.item,
		failureReasonCode: null,
		message: WAKE_PROGRESS_NO_RESPONSE_MESSAGE,
		phase: "failed",
		terminal: true,
		updatedAtMs: input.item.updatedAtMs + activeExpiryMs
	};
}
function resolveWakeProgressActiveExpiryMs(phase) {
	switch (phase) {
		case "request_sent":
		case "queued":
		case "connecting":
		case "retrying": return WAKE_PROGRESS_WAITING_ACTIVE_EXPIRY_MS;
		case "running":
		case "writing":
		case "publishing": return WAKE_PROGRESS_WORKING_ACTIVE_EXPIRY_MS;
		case "sending":
		case "failed":
		case "stopped":
		case "replied": return null;
		default: return assertNever(phase);
	}
}
function findMatchingWakeProgressItemEntry(input) {
	const incomingKeys = listWakeProgressMatchKeys(input.incoming);
	for (const [key, item] of input.itemsByKey) {
		const itemKeys = new Set(listWakeProgressMatchKeys(item));
		if (incomingKeys.some((incomingKey) => itemKeys.has(incomingKey))) return [key, item];
	}
	return null;
}
function listWakeProgressMatchKeys(item) {
	const keys = [];
	if (item.dispatchId) keys.push(`dispatch:${item.dispatchId}`);
	if (item.clientMessageId) keys.push(`client:${item.clientMessageId}:target:${item.targetProfileId}`);
	if (item.sourceSignalId) keys.push(`source:${item.sourceSignalId}:target:${item.targetProfileId}`);
	if (keys.length === 0) keys.push(`target:${item.targetProfileId}`);
	return keys;
}
function buildWakeProgressItemKey(item) {
	if (item.dispatchId) return `dispatch:${item.dispatchId}`;
	if (item.clientMessageId) return `client:${item.clientMessageId}:target:${item.targetProfileId}`;
	if (item.sourceSignalId) return `source:${item.sourceSignalId}:target:${item.targetProfileId}`;
	return `target:${item.targetProfileId}`;
}
function isWakeProgressTerminalSignal(signal) {
	if (signal.type !== "signal.text") return false;
	const purpose = signal.data.meta?.purpose;
	return purpose === AGENT_REPLY_FINAL_PURPOSE || purpose === REMOTE_AGENT_FAILURE_PURPOSE;
}
function doesWakeProgressTerminalSignalMatchItem(signal, item) {
	if (signal.type !== "signal.text") return false;
	const meta = signal.data.meta;
	const targetProfileId = normalizeOptionalText(meta?.targetProfileId) ?? normalizeOptionalText(signal.profile?.profileId);
	if (targetProfileId && targetProfileId !== item.targetProfileId) return false;
	const dispatchId = normalizeOptionalText(meta?.dispatchId);
	if (dispatchId && dispatchId === item.dispatchId) return true;
	const replyToSignalId = normalizeOptionalText(signal.data.messageEnvelope?.replyToSignalId ?? signal.data.replyTo?.signalId);
	return Boolean(replyToSignalId && replyToSignalId === item.sourceSignalId);
}
function formatWakeTargetLabel(value) {
	const normalized = normalizeOptionalText(value) ?? "agent";
	return normalized.startsWith("@") ? normalized : `@${normalized}`;
}
function formatWakeProgressRuntimeLocation(runtimeDeviceDisplayName) {
	const normalized = normalizeOptionalText(runtimeDeviceDisplayName);
	return normalized ? `on ${normalized}` : "on this computer";
}
function normalizeOptionalText(value) {
	const normalized = String(value ?? "").trim();
	return normalized.length > 0 ? normalized : null;
}
function assertNever(value) {
	throw new Error(`Unexpected Wake progress phase: ${String(value)}`);
}
//#endregion
export { SpaceComposerSubmissionError, WAKELOOP_DIRECT_MESSAGE_SPACE_PURPOSE, WAKELOOP_WELCOMING_SPACE_PURPOSE, applyWakeProgressSignals, assertValidSpaceCanonicalMentionSyntax, buildDirectMessagePairKey, buildDispatchResultMessageReadProjection, buildDispatchResultReplyWorkflowProjection, buildRecentMentionRankByProfileId, buildSignalIndex, buildSignalMessageReadProjection, buildSignalReplyWorkflowProjection, buildSpaceMemberAgentOwnerWakeDiagnosticsPresencePresentation, buildSpaceMemberAgentPresencePresentation, buildSpaceMemberAgentPublicCollaborationPresencePresentation, buildSpaceMemberDirectory, buildSpaceMemberDirectoryFromIdentityDirectory, buildSpaceMentionProfileIndex, buildSpaceMessageReadRelationSummary, buildSpaceProfileAddPlan, buildSpaceRoundReadProjection, buildUpdatesReplyWorkflowProjection, buildWakeProgressItemFromTransientSignal, buildWakeProgressMessage, clearWakeProgressItemsForSignal, closeMentionPickerState, collectCanonicalMentionLabelMatches, collectResolvedSpaceMentions, createAnonymousViewerSpaceAccessContext, createAuthenticatedSpaceAccessContext, createDispatchContinuationSpaceAccessContext, createSpaceMemberIdentityIndex, createSpaceTargetCatalog, createWakeProgressItem, enterRelationPickerState, extractMentionAuthoringPrefix, filterVisibleWakeProgressItems, getSpaceAccessPassword, getSpaceAccessRequestContext, getSpaceAccessRequestId, getSpaceProfileAddModeProfileKinds, isAgentDirectConversationSpace, isDirectMessageSpace, isRelationPickerState, isSpaceComposerSubmissionError, isTargetPickerState, isWakeProgressSourceSignalRecoverable, listRecentMentionProfileIdsFromText, listSpaceBareMentionDiagnostics, listSpaceMemberDirectorySendTargets, listSpaceMentionRelationChoices, listSpaceMentionSuggestions, listSpaceSendTargets, listSpaceTargetCatalog, mergeRecentMentionProfileIds, mergeWakeProgressItems, normalizeReadableMentionLookupToken, normalizeRecentMentionProfileIds, openTargetPickerState, prepareHumanSpaceTypedSubmission, prepareOneShotSpaceTypedSubmission, prepareSpaceComposerSubmission, resolveNextRecentMentionProfileIdsForQueuedText, resolveReplyToContext, resolveSpaceDeleteActionVisibility, resolveSpaceMemberAgentOwnerWakeDiagnosticsView, resolveSpaceMemberAgentPublicCollaborationView, resolveSpaceProfileAccent, returnToTargetPickerState, spaceTextHasCanonicalMentionAttempts, spaceTextHasOneShotMentionAttempts, toSpaceAccessAuditFields };

//# sourceMappingURL=index.js.map